import EncryptedStorage from 'react-native-encrypted-storage';
import RNFS from 'react-native-fs';
import { Platform } from 'react-native';
import { tr } from '../i18n/runtime';
import { validationMessageKey, type ValidationErrorBody } from './validationErrors';
import { API_BASE, PUBLIC_WEB_ORIGIN } from '../config';
import {
  appVersion,
  breadcrumb,
  noteRequestId,
  platformName,
  reportError,
} from '../observability/errorReporter';
import { telemetryPath } from '../observability/telemetryPath';
import { isCredentialRejectedHttpStatus } from './authHttp';
import { TimeoutError } from '../util/withTimeout';
import type {
  AuthResponse,
  ChannelDirectoryItem,
  ChannelPostCommentDto,
  ChannelPostCommentPage,
  ChannelPostContent,
  ChannelPostDto,
  ChannelPostListItemDto,
  ChannelPostStatus,
  ChatSummary,
  ContactDto,
  ContactRequestDirection,
  ContactRequestDto,
  ContactRequestStatus,
  GroupMember,
  MessageDto,
  PublicConfig,
  ReminderDto,
  ScheduledMessageDto,
  UpcomingBirthday,
  UserGender,
  UserPublic,
} from './types';

const ACCESS = 'vr_access';
/** Opaque device-bound refresh key (stored in Keychain/Keystore or web localStorage). */
const REFRESH = 'vr_refresh';
const USER = 'vr_user';
let onAuthInvalid: (() => void) | null = null;
/**
 * Кому сообщить, что сохранённый профиль поменялся не по воле этого устройства.
 *
 * <p>Свой профиль клиент знает только из ответа на вход и на обновление токена — отдельной
 * ручки «дай мне меня» нет. Поэтому аватар, поставленный с телефона, для уже открытого веба
 * (и наоборот) существует лишь в виде новой `avatarRevision`, приезжающей вместе с очередным
 * `POST /api/auth/refresh`. Раньше та молча ложилась в хранилище, а на экране оставался
 * профиль, прочитанный при старте: аватар обновлялся «везде, кроме того устройства, где
 * приложение уже было открыто», и только со второй перезагрузки.
 */
let onStoredProfileChanged: ((user: StoredUser) => void) | null = null;

function isWeb(): boolean {
  return Platform.OS === 'web';
}

async function storageGet(key: string): Promise<string | null> {
  if (isWeb()) {
    try {
      if (typeof globalThis.localStorage === 'undefined') return null;
      return globalThis.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  try {
    return await EncryptedStorage.getItem(key);
  } catch {
    return null;
  }
}

async function storageSet(key: string, value: string): Promise<void> {
  if (isWeb()) {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      /* noop */
    }
    return;
  }
  try {
    await EncryptedStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

async function storageDelete(key: string): Promise<void> {
  if (isWeb()) {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      /* noop */
    }
    return;
  }
  try {
    await EncryptedStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

async function readAccess(): Promise<string | null> {
  const raw = await storageGet(ACCESS);
  if (raw == null || raw === '') return null;
  const t = raw.trim();
  return t.length > 0 ? t : null;
}

async function readRefresh(): Promise<string | null> {
  const raw = await storageGet(REFRESH);
  if (raw == null || raw === '') return null;
  const t = raw.trim();
  return t.length > 0 ? t : null;
}

/**
 * Ключа обновления нет — или хранилище просто не отдалось?
 *
 * <p>{@link storageGet} отвечает `null` на оба случая, и для чтения «покажи, что
 * есть» этого достаточно. Перед самим обновлением токена разница принципиальна:
 * «ключа нет» — это конец сессии, восстановить её нечем, и человека надо вывести
 * на экран входа; «хранилище не отдалось» — временная беда (на iOS Keychain
 * молчит, пока телефон ни разу не разблокировали после включения), и выкидывать
 * из аккаунта по ней нельзя.
 */
type RefreshKeyRead = { state: 'present'; token: string } | { state: 'absent' } | { state: 'unreadable' };

async function readRefreshForRenewal(): Promise<RefreshKeyRead> {
  let raw: string | null;
  if (isWeb()) {
    try {
      if (typeof globalThis.localStorage === 'undefined') return { state: 'unreadable' };
      raw = globalThis.localStorage.getItem(REFRESH);
    } catch {
      return { state: 'unreadable' };
    }
  } else {
    try {
      raw = await EncryptedStorage.getItem(REFRESH);
    } catch {
      // Отказ хранилища трактуем как «пока не знаем»: следующая попытка прочтёт снова.
      return { state: 'unreadable' };
    }
  }
  const token = raw?.trim();
  return token ? { state: 'present', token } : { state: 'absent' };
}

export type StoredUser = {
  userId: string;
  login: string;
  displayName: string;
  appRole: 'USER' | 'BOSS';
  /** legacy / internal */
  email?: string;
  uiLocale?: string | null;
  hasAvatar?: boolean;
  avatarRevision?: number;
  avatarShape?: string | null;
  publicHandle?: string | null;
  profilePublic?: boolean;
  allowDiscoveryByHandle?: boolean;
  bio?: string | null;
  statusText?: string | null;
  /** «Не беспокоить»: пуши не приходят вовсе. Решает сервер, здесь — его ответ. */
  doNotDisturb?: boolean;
  /** Текст сообщения в пушах (PRIV-5). Решает сервер, здесь — его ответ. */
  showMessageText?: boolean;
  gender?: UserGender | null;
  /** Своя дата рождения целиком, ISO `yyyy-MM-dd`; чужим видны только день, месяц и возраст. */
  birthDate?: string | null;
};

export async function saveTokens(access: string, refresh: string): Promise<void> {
  await storageSet(ACCESS, access);
  await storageSet(REFRESH, refresh);
}

export async function saveUserProfile(user: StoredUser): Promise<void> {
  await storageSet(USER, JSON.stringify(user));
}

export async function loadStoredUser(): Promise<StoredUser | null> {
  const raw = await storageGet(USER);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredUser> & { email?: string };
    if (!parsed.login && parsed.email) {
      parsed.login = parsed.email.includes('@') ? parsed.email.split('@')[0] : parsed.email;
    }
    if (!parsed.appRole) {
      parsed.appRole = 'USER';
    }
    if (!parsed.login) {
      return null;
    }
    return parsed as StoredUser;
  } catch {
    return null;
  }
}

export async function clearTokens(): Promise<void> {
  await storageDelete(ACCESS);
  await storageDelete(REFRESH);
  await storageDelete(USER);
}

/**
 * Wipe stored credentials if this is the first launch after a fresh install.
 *
 * iOS Keychain entries (and therefore everything in
 * `react-native-encrypted-storage`) survive `app delete → reinstall` by
 * default. That means a user who reinstalls the app stays "logged in" with
 * a stale access/refresh pair that has been invalidated server-side, and
 * the boot path keeps trying to refresh tokens that are no longer valid.
 *
 * We detect first-launch via a marker file in the sandboxed Documents
 * directory (which IS wiped on uninstall on iOS). If the marker is absent
 * we wipe the Keychain entries before the AuthProvider reads them. On
 * Android the same logic is a defensive no-op since SharedPreferences is
 * wiped on uninstall too, but we keep it for symmetry / explicit behaviour.
 *
 * Returns `true` if a wipe was performed.
 */
export async function maybeWipeKeychainOnFirstLaunch(): Promise<boolean> {
  if (isWeb()) return false;
  // RNFS is imported lazily so the web bundle is unaffected.
  let RNFS: typeof import('react-native-fs');
  try {
    RNFS = await import('react-native-fs');
  } catch {
    return false;
  }
  const markerPath = `${RNFS.DocumentDirectoryPath}/.vrmsgr-install-marker`;
  try {
    const exists = await RNFS.exists(markerPath);
    if (exists) return false;
  } catch {
    return false;
  }
  // First launch after install (or after the user cleared app data): the
  // marker doesn't exist yet. Wipe stale Keychain entries from a previous
  // installation BEFORE anything else reads them.
  try {
    await clearTokens();
  } catch {
    /* best-effort */
  }
  try {
    await RNFS.writeFile(markerPath, String(Date.now()), 'utf8');
  } catch {
    // If we can't write the marker we'll just wipe again next boot; that's
    // harmless if the user is already logged out, and self-healing if for
    // some reason the marker write fails repeatedly.
  }
  return true;
}

export function setAuthInvalidHandler(handler: (() => void) | null): void {
  onAuthInvalid = handler;
}

function notifyAuthInvalid(): void {
  onAuthInvalid?.();
}

/** См. {@link onStoredProfileChanged}. Подписчик один — `AuthProvider`. */
export function setStoredProfileChangedHandler(
  handler: ((user: StoredUser) => void) | null,
): void {
  onStoredProfileChanged = handler;
}

export async function loadStoredAccess(): Promise<string | null> {
  return readAccess();
}

export async function loadStoredRefresh(): Promise<string | null> {
  return readRefresh();
}

type SilentRefreshResult =
  | { status: 'refreshed'; accessToken: string }
  | { status: 'invalid_refresh' }
  | { status: 'transient_error' };

type PerformRefreshResult =
  | { ok: true; accessToken: string }
  | { ok: false; clearedSession: boolean };

/**
 * Coalesces concurrent token-refresh attempts into a single in-flight request.
 *
 * Without this, every concurrent 401 triggers its own POST /api/auth/refresh.
 * Because the server rotates the refresh token on first use, only the first
 * caller gets a new token pair — all later callers send the already-invalidated
 * token and receive 401, which then calls notifyAuthInvalid() and logs the user
 * out. With the singleton, everyone awaits the same Promise and shares the result.
 */
let refreshInFlight: Promise<PerformRefreshResult> | null = null;

function performTokenRefresh(): Promise<PerformRefreshResult> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doTokenRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doTokenRefresh(): Promise<PerformRefreshResult> {
  const stored = await readRefreshForRenewal();
  if (stored.state === 'unreadable') {
    return { ok: false, clearedSession: false };
  }
  if (stored.state === 'absent') {
    /*
      Обновлять нечем — сессии больше нет, и следующий запрос получит тот же 401.
      Раньше этот случай считался временной ошибкой: приложение оставалось «в
      аккаунте», молча собирало 401 на каждом запросе и бесконечно переподключало
      веб-сокет. В журнале сервера это выглядело как `WS handshake rejected:
      invalid bearer token` раз в пятнадцать секунд часами подряд с одного
      устройства — и ни одного `POST /api/auth/refresh` рядом, потому что запрос
      даже не отправлялся. Человек при этом видел приложение без единого чата и
      никакого объяснения. Правильный конец — вывести на экран входа.
    */
    await clearTokens();
    notifyAuthInvalid();
    return { ok: false, clearedSession: true };
  }
  const refresh = stored.token;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
  } catch {
    return { ok: false, clearedSession: false };
  }
  if (res.status === 401) {
    await clearTokens();
    notifyAuthInvalid();
    return { ok: false, clearedSession: true };
  }
  if (!res.ok) {
    return { ok: false, clearedSession: false };
  }
  let body: AuthResponse;
  try {
    body = (await parseJsonBody(res)) as AuthResponse;
  } catch {
    return { ok: false, clearedSession: false };
  }
  await saveTokens(body.accessToken, body.refreshToken);
  const prev = await loadStoredUser();
  if (prev && String(body.userId) === prev.userId) {
    const email = body.email ?? prev.email;
    let login = body.login ?? prev.login;
    if (!login && email && email.includes('@')) {
      login = email.split('@')[0];
    }
    const next: StoredUser = {
      ...prev,
      login,
      displayName: body.displayName ?? prev.displayName,
      appRole: body.appRole ?? prev.appRole,
      uiLocale: body.uiLocale ?? prev.uiLocale,
      email,
      hasAvatar: body.hasAvatar ?? prev.hasAvatar,
      avatarRevision: body.avatarRevision ?? prev.avatarRevision,
      avatarShape: body.avatarShape ?? prev.avatarShape,
      publicHandle: body.publicHandle ?? prev.publicHandle,
      profilePublic: typeof body.profilePublic === 'boolean' ? body.profilePublic : prev.profilePublic,
      allowDiscoveryByHandle:
        typeof body.allowDiscoveryByHandle === 'boolean'
          ? body.allowDiscoveryByHandle
          : prev.allowDiscoveryByHandle,
      bio: body.bio !== undefined ? body.bio : prev.bio,
      statusText: body.statusText !== undefined ? body.statusText : prev.statusText,
    };
    await saveUserProfile(next);
    onStoredProfileChanged?.(next);
  }
  return { ok: true, accessToken: body.accessToken };
}

export async function trySilentRefresh(): Promise<SilentRefreshResult> {
  const r = await performTokenRefresh();
  if (r.ok) {
    return { status: 'refreshed', accessToken: r.accessToken };
  }
  if (r.clearedSession) {
    return { status: 'invalid_refresh' };
  }
  return { status: 'transient_error' };
}

type ApiFetchOptions = {
  retry?: boolean;
  /**
   * После 401 и refresh токена пересобрать multipart вместо {@link cloneFormDataForRetry}:
   * в RN части файла `{ uri, name, type }` при forEach-клонировании теряются и Spring
   * отвечает MissingServletRequestPartException (нет part `file`).
   */
  rebuildFormData?: () => FormData;
  /** Переопределить таймаут запроса (мс). По умолчанию {@link API_FETCH_TIMEOUT_MS}. */
  timeoutMs?: number;
  /**
   * Коды, которые для этого запроса — обычный ответ, а не сбой: остаются в шагах отчёта, но
   * сами отчётом не уходят. Например, 404 у «идёт ли в чате звонок» значит «не идёт».
   */
  expectedStatuses?: readonly number[];
};

/**
 * Повторный fetch с тем же экземпляром FormData после 401/refresh: на вебе и в RN
 * (Hermes) тело часто уже «прочитано» — второй запрос уходит без part `file`,
 * Spring отвечает MissingServletRequestPartException.
 */
function cloneFormDataForRetry(body: FormData): FormData {
  const next = new FormData();
  body.forEach((value, key) => {
    next.append(key, value);
  });
  return next;
}

const API_FETCH_TIMEOUT_MS = 30_000;
// Large files (video / audio) on a slow mobile connection can take several minutes to upload.
const UPLOAD_FETCH_TIMEOUT_MS = 10 * 60 * 1000;

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (e) {
    // Сигнал наш и отменяется только таймером: отмена здесь — это таймаут, а не «Aborted»
    // (аудит 2026-10-17, UX-52). TimeoutError человек видит как «нет связи».
    if (controller.signal.aborted) throw new TimeoutError(timeoutMs);
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Чем представилось приложение. Сервер кладёт это в каждую свою строку журнала (OBS-1), и без
 * него «у тестера не работает» не отличить от «в вебе не работает»: до сих пор оба случая
 * выглядели в логе одинаково.
 */
const CLIENT_HEADER = `${platformName()}/${appVersion()}`;

/**
 * Исход запроса — в шаги отчёта, а неудача — ещё и в него сам (OBS-2).
 *
 * <p>`requestId` из заголовка ответа запоминается всегда, даже для успешных запросов: поломка
 * рождается через мгновение после запроса, и по этому значению серверная сторона случая находится
 * в журнале одной командой.
 *
 * <p>Уровень — `warning`: неудачный запрос это ожидаемая часть жизни мобильного приложения (сеть
 * пропала, токен протух), и поднимать телеграм-бота на каждый 401 нельзя. 5xx — исключение:
 * сломались мы, и об этом надо знать.
 */
function noteResponse(
  method: string,
  path: string,
  res: Response,
  expectedStatuses: readonly number[] = [],
): void {
  try {
    noteRequestId(res.headers?.get?.('X-Request-Id'));
    // Только шаблон пути (PRIV-3): в query — поисковые запросы и ссылки из чатов, в пути —
    // секреты встреч, а шаги уезжают с каждым отчётом в журнал, GlitchTip и Telegram.
    const where = telemetryPath(path);
    if (res.ok) {
      breadcrumb('app', `${method} ${where} → ${res.status}`);
      return;
    }
    breadcrumb('app', `${method} ${where} → ${res.status}`);
    // Превью ссылки не открылось — значит, не ответил чужой сайт, а не мы: карточки просто
    // не будет. Каждая ссылка на закрытую страницу или файл иначе будила бы владельца.
    if (path.startsWith('/api/link-preview')) return;
    if (expectedStatuses.includes(res.status)) return;
    void reportError('app', new Error(`HTTP ${res.status} ${method} ${where}`), {
      level: res.status >= 500 ? 'error' : 'warning',
      context: { метод: method, путь: where, код: res.status },
    });
  } catch {
    /* отчёт о поломке не имеет права быть поломкой */
  }
}

export async function apiFetch(path: string, init: RequestInit, options: ApiFetchOptions = {}): Promise<Response> {
  const {
    retry = true,
    rebuildFormData,
    timeoutMs = API_FETCH_TIMEOUT_MS,
    expectedStatuses = [],
  } = options;
  let token = await readAccess();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Client', CLIENT_HEADER);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const method = (init.method ?? 'GET').toUpperCase();
  let res: Response;
  try {
    res = await fetchWithTimeout(`${API_BASE}${path}`, { ...init, headers }, timeoutMs);
  } catch (networkError) {
    // Сеть не дошла до сервера вовсе: обрыв связи, таймаут, отменённый запрос. Серверного следа у
    // такого случая нет по определению — значит, единственный след может оставить только клиент.
    breadcrumb('app', `${method} ${telemetryPath(path)} → нет сети`);
    void reportError('app', networkError, {
      level: 'warning',
      context: { метод: method, путь: telemetryPath(path), исход: 'сеть-недоступна' },
    });
    throw networkError;
  }
  noteResponse(method, path, res, expectedStatuses);
  let hadTransientRefresh = false;
  if (isCredentialRejectedHttpStatus(res.status) && retry) {
    const fr = await performTokenRefresh();
    if (fr.ok) {
      token = fr.accessToken;
      const h2 = new Headers(init.headers);
      h2.set('Accept', 'application/json');
      h2.set('X-Client', CLIENT_HEADER);
      h2.set('Authorization', `Bearer ${token}`);
      const retryBody =
        init.body instanceof FormData
          ? rebuildFormData
            ? rebuildFormData()
            : cloneFormDataForRetry(init.body)
          : init.body;
      res = await fetchWithTimeout(`${API_BASE}${path}`, { ...init, headers: h2, body: retryBody }, timeoutMs);
      noteResponse(method, path, res, expectedStatuses);
    } else if (fr.clearedSession) {
      return res;
    } else {
      hadTransientRefresh = true;
    }
  }
  if (isCredentialRejectedHttpStatus(res.status) && !hadTransientRefresh) {
    // Ровно та секунда, в которую человек «внезапно вылетел из аккаунта». До сих пор она не
    // оставляла следа ни на клиенте, ни на сервере — 401 приходил и приходил.
    breadcrumb('auth', 'сессия сброшена после отказа обновить токен');
    void reportError('auth', new Error('session cleared after refresh failure'), {
      level: 'warning',
      context: { метод: method, путь: telemetryPath(path), код: res.status },
    });
    await clearTokens();
    notifyAuthInvalid();
  }
  return res;
}

type UploadProgressListener = (fraction: number) => void;

type RawResponse = { status: number; body: string };

/**
 * `fetch` cannot report *upload* progress — neither in the browser nor in React
 * Native — so the progress-reporting path goes through XMLHttpRequest instead.
 * RN implements `upload.onprogress` on top of its `didSendNetworkData` event, so
 * the same code works on web, iOS and Android.
 */
function xhrUpload(
  url: string,
  form: FormData,
  token: string | null,
  timeoutMs: number,
  onProgress?: UploadProgressListener,
): Promise<RawResponse> {
  return new Promise<RawResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = timeoutMs;
    xhr.setRequestHeader('Accept', 'application/json');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    // Content-Type is deliberately left alone: the boundary is filled in by the
    // XHR implementation, and setting it by hand breaks the multipart parser.
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e: ProgressEvent) => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.max(0, Math.min(1, e.loaded / e.total)));
        }
      };
    }
    xhr.onload = () =>
      resolve({
        status: xhr.status,
        body: typeof xhr.responseText === 'string' ? xhr.responseText : '',
      });
    xhr.onerror = () => reject(new Error(tr('errorGeneric')));
    xhr.ontimeout = () => reject(new Error(tr('errorGeneric')));
    xhr.onabort = () => reject(new Error(tr('errorGeneric')));
    xhr.send(form);
  });
}

/**
 * XHR twin of {@link apiFetch} for multipart uploads: same 401 → refresh →
 * retry-once handling, but the body is rebuilt from scratch on the retry (an
 * already-sent FormData loses its RN file part) and progress is streamed to the
 * caller.
 */
async function apiUpload(
  path: string,
  buildFormData: () => FormData,
  opts: { timeoutMs: number; onProgress?: UploadProgressListener },
): Promise<RawResponse> {
  const url = `${API_BASE}${path}`;
  const token = await readAccess();
  let res = await xhrUpload(url, buildFormData(), token, opts.timeoutMs, opts.onProgress);
  let hadTransientRefresh = false;
  if (isCredentialRejectedHttpStatus(res.status)) {
    const fr = await performTokenRefresh();
    if (fr.ok) {
      // The first attempt already reported progress; restart the bar for the retry.
      opts.onProgress?.(0);
      res = await xhrUpload(url, buildFormData(), fr.accessToken, opts.timeoutMs, opts.onProgress);
    } else if (fr.clearedSession) {
      return res;
    } else {
      hadTransientRefresh = true;
    }
  }
  if (isCredentialRejectedHttpStatus(res.status) && !hadTransientRefresh) {
    await clearTokens();
    notifyAuthInvalid();
  }
  return res;
}

type DirectUploadTicket = {
  uploadUrl: string;
  contentType: string;
  ticket: string;
  expiresAtEpochSeconds: number;
};

/**
 * Ask the backend for a slot to PUT bytes straight to the storage provider.
 *
 * `null` means "not available, use the multipart endpoint": either the backend
 * stores attachments on its own filesystem (no pre-signing), or the request
 * failed. Never throws — a broken fast path must degrade to the working slow
 * path rather than fail the send.
 */
async function requestDirectUploadTicket(
  chatId: string,
  mimeType: string,
  sizeBytes: number | null,
): Promise<DirectUploadTicket | null> {
  try {
    const res = await apiFetch(`/api/chats/${chatId}/attachments/direct-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mimeType, sizeBytes }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<DirectUploadTicket>;
    if (!body.uploadUrl || !body.ticket || !body.contentType) return null;
    return body as DirectUploadTicket;
  } catch {
    return null;
  }
}

/**
 * Native-only direct upload: RNFS streams the file straight to Cloudflare with
 * `PUT` + `binaryStreamOnly`, so the bytes never traverse our VPS.
 *
 * Not used on web: the R2 bucket serves no CORS headers and our object-scoped
 * keys cannot configure bucket-level CORS, so a browser PUT would be blocked
 * (the same reason the download path streams through the backend for web).
 */
async function putFileDirect(
  ticket: DirectUploadTicket,
  filePath: string,
  filename: string,
  onProgress?: UploadProgressListener,
): Promise<void> {
  const { promise } = RNFS.uploadFiles({
    toUrl: ticket.uploadUrl,
    method: 'PUT',
    binaryStreamOnly: true,
    headers: { 'Content-Type': ticket.contentType },
    files: [{ name: 'file', filename, filepath: filePath, filetype: ticket.contentType }],
    progress: (p) => {
      if (onProgress && p.totalBytesExpectedToSend > 0) {
        onProgress(
          Math.max(0, Math.min(1, p.totalBytesSent / p.totalBytesExpectedToSend)),
        );
      }
    },
  });
  const result = await promise;
  if (result.statusCode < 200 || result.statusCode >= 300) {
    throw new Error(`Direct upload rejected by storage (${result.statusCode})`);
  }
}

/** Register the directly-uploaded object; the server reads the real size back from storage. */
async function commitDirectUpload(
  chatId: string,
  ticket: string,
  opts: { mimeType?: string; durationMs?: number; thumbnailBase64?: string },
): Promise<AttachmentMeta> {
  const res = await apiFetch(`/api/chats/${chatId}/attachments/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ticket,
      mimeType: opts.mimeType,
      durationMs:
        typeof opts.durationMs === 'number' && Number.isFinite(opts.durationMs)
          ? Math.max(0, Math.round(opts.durationMs))
          : undefined,
      thumbnailBase64: opts.thumbnailBase64,
    }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Upload failed'));
  return (await res.json()) as AttachmentMeta;
}

type TurnIceCredentialsResponse = {
  enabled: boolean;
  ttlSeconds: number;
  expiresAtEpochSeconds: number;
  iceServers: Array<{ urls: string | string[]; username?: string; credential?: string }>;
};

/** Coturn ephemeral credentials from Spring. Returns null on network/HTTP errors. */
export async function fetchTurnIceCredentials(): Promise<TurnIceCredentialsResponse | null> {
  try {
    const res = await apiFetch('/api/turn/credentials', { method: 'GET' });
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as TurnIceCredentialsResponse;
  } catch {
    return null;
  }
}

type LiveKitGroupTokenResponse = {
  enabled: boolean;
  url: string;
  token: string;
  roomName: string;
  tokenExpiresAtEpochSeconds: number;
};

/**
 * Почему не `LiveKitGroupTokenResponse | null`, как было раньше: `null` означал сразу три разные
 * вещи — «LiveKit не настроен», «сервер ответил ошибкой» и «сети нет». Вызывающий не мог их
 * различить и на любой из них молча уходил в mesh, который для группы не работает вовсе.
 * Теперь причина доезжает до UI (стори 7.3).
 */
type LiveKitTokenResult =
  | { kind: 'ok'; creds: LiveKitGroupTokenResponse }
  | { kind: 'disabled' }
  | { kind: 'error'; status?: number };

/** Group-call SFU token for a server-side call session. */
export async function fetchLiveKitCallSessionToken(callSessionId: string): Promise<LiveKitTokenResult> {
  try {
    const q = new URLSearchParams({ callSessionId });
    const res = await apiFetch(`/api/livekit/token?${q.toString()}`, { method: 'GET' });
    if (!res.ok) {
      return { kind: 'error', status: res.status };
    }
    const creds = (await res.json()) as LiveKitGroupTokenResponse;
    if (!creds?.enabled || !creds.token || !creds.url) {
      return { kind: 'disabled' };
    }
    return { kind: 'ok', creds };
  } catch {
    return { kind: 'error' };
  }
}

/** Серверная сессия звонка — единственный источник истины о том, идёт ли в чате разговор. */
type CallParticipantSummary = {
  userId: string;
  displayName?: string;
  joinedAt: string;
};

export type CallSessionSummary = {
  id: string;
  chatId: string;
  startedBy: string;
  mode: 'audio' | 'video';
  scope: 'direct' | 'group';
  status: 'ACTIVE' | 'ENDED';
  startedAt: string;
  /** Только в ответе на свой запрос; в широковещательном кадре его нет. */
  joined?: boolean;
  participants: CallParticipantSummary[];
};

/**
 * Начать звонок или присоединиться к идущему — одна ручка на оба случая, потому что клиент не
 * может знать наверняка, идёт ли уже разговор, а гонку «проверил → начал» иначе пришлось бы
 * решать на клиенте (и она бы не решалась: два устройства проверяют одновременно).
 */
export async function startOrJoinCall(
  chatId: string,
  mode: 'audio' | 'video',
  scope: 'direct' | 'group',
): Promise<CallSessionSummary> {
  const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/call`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode, scope }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to start the call'));
  return res.json();
}

/** Активный звонок в чате или `null`, если его нет. */
export async function fetchActiveCall(chatId: string): Promise<CallSessionSummary | null> {
  try {
    // 404 здесь — «звонка нет», а не сбой: запрос уходит при каждом открытии чата, и без
    // этого каждый чат без звонка оставлял отчёт об ошибке.
    const res = await apiFetch(
      `/api/chats/${encodeURIComponent(chatId)}/call`,
      { method: 'GET' },
      { expectedStatuses: [404] },
    );
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return (await res.json()) as CallSessionSummary;
  } catch {
    return null;
  }
}

/**
 * Отклонить входящий без сокета — «Отклонить» в шторке Android, когда приложение свёрнуто или
 * убито. Сервер рассылает тот же сигнал `decline`, что и сокет (CallDeclineController).
 */
export async function declineIncomingCall(chatId: string, callInviteId: string | null): Promise<void> {
  const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/call/decline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(callInviteId ? { callInviteId } : {}),
  });
  if (!res.ok) throw new Error(await readError(res, 'Decline failed'));
}

/** Выход из звонка. Последний вышедший закрывает сессию на сервере. */
export async function leaveCall(chatId: string): Promise<void> {
  try {
    await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/call/leave`, { method: 'POST' });
  } catch {
    // Разрыв на выходе не должен ронять teardown звонка: сессию доберёт серверная уборка
    // осиротевших сессий (CallSessionService.closeStaleSessions).
  }
}

/** Пока открыт экран звонка — иначе сервер закроет сессию как осиротевшую через две минуты. */
export async function sendCallHeartbeat(chatId: string): Promise<void> {
  try {
    await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/call/heartbeat`, { method: 'POST' });
  } catch {
    // Пропущенный heartbeat не повод шуметь: их четыре подряд до закрытия сессии.
  }
}

/**
 * Lightweight auth probe. Does not log the user out on network errors or when
 * refresh is temporarily unreachable (still has a valid device key).
 */
export async function probeAuth(): Promise<boolean> {
  try {
    let token = await readAccess();
    const headers = new Headers({ Accept: 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    let res = await fetch(`${API_BASE}/api/chats`, { method: 'GET', headers });
    if (res.ok) return true;
    if (!isCredentialRejectedHttpStatus(res.status)) {
      return false;
    }
    const fr = await performTokenRefresh();
    if (!fr.ok) {
      if (fr.clearedSession) return false;
      return true;
    }
    token = fr.accessToken;
    const h2 = new Headers({ Accept: 'application/json' });
    h2.set('Authorization', `Bearer ${token}`);
    res = await fetch(`${API_BASE}/api/chats`, { method: 'GET', headers: h2 });
    return res.ok;
  } catch {
    return true;
  }
}

function errorFromBody(text: string, fallback: string): string {
  if (!text) return fallback;
  try {
    const j = JSON.parse(text) as ValidationErrorBody;
    // AUTH-14. Отказ проверки тела приходит признаками (`field`, `rule`), а не готовой фразой:
    // готовую сервер сочинял на языке, который выбирал сам по Accept-Language запроса, и мог
    // прислать китайский в приложение с четырьмя языками. Слова подбираем своим словарём.
    const key = validationMessageKey(j);
    if (key) return tr(key);
    return j.error ?? fallback;
  } catch {
    return fallback;
  }
}

export async function readError(res: Response, fallback: string): Promise<string> {
  return errorFromBody(await res.text(), fallback);
}

/**
 * Отказ сервера вместе с кодом ответа. `message` — тот же текст, что и раньше (старые
 * обработчики показывают его как есть), а код нужен экранам, которые должны отличить один
 * отказ от другого: «неверный текущий пароль» (403) от прочего, «ссылка протухла» (410) от
 * «ссылка не наша» (400) — аудит удобства 2026-10-04, п. 9 и 39.
 */
export class HttpStatusError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpStatusError';
    this.status = status;
  }
}

async function httpStatusError(res: Response, fallback: string): Promise<HttpStatusError> {
  return new HttpStatusError(res.status, await readError(res, fallback));
}

/** Ответ не JSON (часто HTML от dev-сервера или 404-страница). */
function throwNonJsonResponse(): never {
  throw new Error(tr('nonJsonServer'));
}

async function parseJsonBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    throwNonJsonResponse();
  }
}

export type BossUserRow = {
  id: string;
  login: string;
  displayName: string;
  appRole: string;
};

export async function fetchBossUsers(): Promise<BossUserRow[]> {
  const res = await apiFetch('/api/boss/users', { method: 'GET' });
  if (!res.ok) {
    throw new Error(await readError(res, tr('bossUsersLoadFailed')));
  }
  const raw: unknown = await res.json();
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const o = row as Record<string, unknown>;
    return {
      id: o.id != null ? String(o.id) : '',
      login: o.login != null ? String(o.login) : '',
      displayName: o.displayName != null ? String(o.displayName) : '',
      appRole: o.appRole != null ? String(o.appRole) : 'USER',
    };
  });
}

export async function bossCreateUser(payload: {
  login: string;
  password: string;
  displayName: string;
  email?: string;
  /** Язык приветствия в ЛС: ru | en | sr */
  instructionLocale?: string;
}): Promise<BossUserRow> {
  const body: Record<string, unknown> = {
    login: payload.login,
    password: payload.password,
    displayName: payload.displayName,
  };
  if (payload.email) {
    body.email = payload.email;
  }
  if (payload.instructionLocale) {
    body.instructionLocale = payload.instructionLocale;
  }
  const res = await apiFetch('/api/boss/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('bossCreateUserFailed')));
  }
  return res.json() as Promise<BossUserRow>;
}

export async function bossDeleteUser(userId: string): Promise<void> {
  const res = await apiFetch(`/api/boss/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(await readError(res, tr('bossDeleteUserFailed')));
  }
}

/**
 * Смена собственного пароля (стори 18.5).
 *
 * <p>Возвращает новую пару токенов: сервер при смене пароля отзывает все сессии и удаляет
 * refresh-токены — иначе устройство, с которого её сделали, тут же разлогинилось бы.
 */
export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
  const res = await apiFetch('/api/users/me/password', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    throw await httpStatusError(res, tr('passwordChangeFailed'));
  }
  return (await parseJsonBody(res)) as AuthResponse;
}

/**
 * Удаление собственного аккаунта (REL-1) — требование Apple 5.1.1(v) и Google Play.
 *
 * Возвращать нечего: сервер отвечает 204, а сессии в этот момент уже отозваны. Вызывающий
 * обязан следом разлогинить приложение — токен в памяти формально ещё жив, но за ним больше
 * ничего нет.
 *
 * Пароль уходит телом DELETE, а не в query: в строке запроса он осел бы в логах nginx.
 */
/**
 * Блокировка пользователя (REL-2). Apple 1.2 требует её от любого приложения с
 * пользовательским контентом — наравне с механизмом жалобы.
 */
export async function blockUser(userId: string): Promise<void> {
  const res = await apiFetch('/api/blocks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('blockFailed')));
  }
}

export async function unblockUser(userId: string): Promise<void> {
  const res = await apiFetch(`/api/blocks/${userId}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(await readError(res, tr('unblockFailed')));
  }
}

export type BlockedUserCard = {
  userId: string;
  displayName: string;
  hasAvatar: boolean;
  avatarRevision: number;
  avatarShape: string;
  publicHandle: string | null;
};

export async function listBlockedUsers(): Promise<BlockedUserCard[]> {
  const res = await apiFetch('/api/blocks', { method: 'GET' });
  if (!res.ok) {
    throw new Error(await readError(res, tr('blockedListLoadFailed')));
  }
  return ((await parseJsonBody(res)) as BlockedUserCard[]) ?? [];
}

/**
 * Жалоба на пользователя или сообщение (REL-2).
 *
 * Ответ пустой намеренно: идентификатор дела человеку не нужен, а повторное нажатие сервер
 * гасит сам — второе «Пожаловаться» на то же сообщение не заводит второе дело.
 */
export async function reportAbuse(params: {
  targetUserId: string;
  chatId?: string | null;
  messageId?: string | null;
  category: string;
  comment?: string | null;
}): Promise<void> {
  const res = await apiFetch('/api/reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      targetUserId: params.targetUserId,
      chatId: params.chatId ?? null,
      messageId: params.messageId ?? null,
      category: params.category,
      comment: params.comment ?? null,
    }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('reportFailed')));
  }
}

export async function deleteOwnAccount(password: string): Promise<void> {
  const res = await apiFetch('/api/users/me', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    throw await httpStatusError(res, tr('settingsDeleteAccountFailed'));
  }
}

/**
 * Правка профиля. Ручка перезаписывает его целиком, поэтому вызывающий обязан передать и те
 * поля, которых не касался (см. `savedProfile` в SettingsModal) — включая пол и дату рождения:
 * пропущенные, они снимутся.
 */
export async function updateOwnProfile(
  login: string,
  displayName: string,
  bio?: string | null,
  statusText?: string | null,
  gender?: UserGender | null,
  birthDate?: string | null,
): Promise<void> {
  const res = await apiFetch('/api/users/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      login,
      displayName,
      bio: bio ?? null,
      statusText: statusText ?? null,
      gender: gender ?? null,
      birthDate: birthDate ?? null,
    }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('profileUpdateFailed')));
  }
}

/**
 * Часовой пояс устройства — фоном, при старте и при смене.
 *
 * <p>Не настройка человека, а факт о его телефоне: без него сервер не знает, когда у него
 * утро, и напоминание о дне рождения контакта приходило бы всем в один час по Белграду.
 *
 * <p>Молчит при любой ошибке: это фоновый запрос, и падать из-за него на старте приложения
 * нечему.
 */
export async function updateTimeZoneOnServer(timeZone: string): Promise<void> {
  try {
    await apiFetch('/api/users/me/time-zone', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timeZone }),
    });
  } catch {
    /* фоновый запрос: неудача ничего не ломает, повторится при следующем старте */
  }
}

/** Ближайшие дни рождения контактов. `today` — дата глазами клиента, см. UpcomingBirthday. */
export async function fetchUpcomingBirthdays(today: string): Promise<UpcomingBirthday[]> {
  const res = await apiFetch(`/api/birthdays/upcoming?today=${encodeURIComponent(today)}`, {});
  if (!res.ok) throw new Error(await readError(res, tr('errorGeneric')));
  const raw = (await parseJsonBody(res)) as UpcomingBirthday[] | null;
  return Array.isArray(raw) ? raw : [];
}

/**
 * Опись музыкального плеера (2026-10-05): плейлисты и отпечатки треков, без самой музыки.
 * `null` — описи на сервере ещё нет (новый аккаунт, сервер ответил 204).
 */
export async function fetchMusicLibrary(): Promise<{ revision: number; payload: unknown } | null> {
  const res = await apiFetch('/api/music/library', {});
  if (res.status === 204) return null;
  if (!res.ok) throw new HttpStatusError(res.status, await readError(res, tr('errorGeneric')));
  const raw = (await parseJsonBody(res)) as { revision?: number; payload?: unknown } | null;
  return raw && typeof raw.revision === 'number' ? { revision: raw.revision, payload: raw.payload } : null;
}

export async function putMusicLibrary(payload: object): Promise<number> {
  const res = await apiFetch('/api/music/library', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payload }),
  });
  if (!res.ok) throw new HttpStatusError(res.status, await readError(res, tr('errorGeneric')));
  const raw = (await parseJsonBody(res)) as { revision?: number } | null;
  return typeof raw?.revision === 'number' ? raw.revision : 0;
}

export async function fetchReminders(): Promise<ReminderDto[]> {
  const res = await apiFetch('/api/reminders', {});
  if (!res.ok) throw new Error(await readError(res, tr('errorGeneric')));
  const raw = (await parseJsonBody(res)) as ReminderDto[] | null;
  return Array.isArray(raw) ? raw : [];
}

export async function createReminder(body: {
  title: string;
  body?: string | null;
  fireAt: string;
  timeZone: string;
  chatId?: string | null;
  messageId?: string | null;
}): Promise<ReminderDto> {
  const res = await apiFetch('/api/reminders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, tr('reminderSaveFailed')));
  return (await parseJsonBody(res)) as ReminderDto;
}

export async function updateReminder(
  reminderId: string,
  body: { title: string; body?: string | null; fireAt: string; timeZone: string },
): Promise<ReminderDto> {
  const res = await apiFetch(`/api/reminders/${encodeURIComponent(reminderId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, tr('reminderSaveFailed')));
  return (await parseJsonBody(res)) as ReminderDto;
}

/** «Отложить»: минуты от «сейчас» по часам сервера — телефонные вправе отставать. */
export async function snoozeReminder(reminderId: string, minutes: number): Promise<ReminderDto> {
  const res = await apiFetch(`/api/reminders/${encodeURIComponent(reminderId)}/snooze`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ minutes }),
  });
  if (!res.ok) throw new Error(await readError(res, tr('reminderSaveFailed')));
  return (await parseJsonBody(res)) as ReminderDto;
}

export async function deleteReminder(reminderId: string): Promise<void> {
  const res = await apiFetch(`/api/reminders/${encodeURIComponent(reminderId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, tr('errorGeneric')));
}

export async function fetchScheduledMessages(): Promise<ScheduledMessageDto[]> {
  const res = await apiFetch('/api/scheduled-messages', {});
  if (!res.ok) throw new Error(await readError(res, tr('errorGeneric')));
  const raw = (await parseJsonBody(res)) as ScheduledMessageDto[] | null;
  return Array.isArray(raw) ? raw : [];
}

/**
 * Запланировать отправку одному или нескольким адресатам.
 *
 * <p>Ответ — список строк, по одной на адресата: у каждой свой статус, потому что уйти одному
 * и не уйти другому — обычный исход, который одним статусом не описать.
 */
export async function createScheduledMessage(body: {
  recipientUserIds?: string[];
  chatIds?: string[];
  ciphertext: string;
  sendAt: string;
  timeZone: string;
}): Promise<ScheduledMessageDto[]> {
  const res = await apiFetch('/api/scheduled-messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, tr('scheduledMessageSaveFailed')));
  const raw = (await parseJsonBody(res)) as ScheduledMessageDto[] | null;
  return Array.isArray(raw) ? raw : [];
}

/** Отмена всей отправки: одна карточка списка — одно нажатие, а не по строке на адресата. */
export async function cancelScheduledMessageBatch(batchId: string): Promise<void> {
  const res = await apiFetch(`/api/scheduled-messages/batch/${encodeURIComponent(batchId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await readError(res, tr('scheduledMessageCancelFailed')));
}

type PatchUserShareSettingsBody = {
  publicHandle?: string | null;
  profilePublic?: boolean;
  allowDiscoveryByHandle?: boolean;
};

/** Сохранённое состояние: ник здесь канонический, приведённый сервером (стори 18.2). */
type SavedShareSettings = {
  publicHandle: string | null;
  profilePublic: boolean;
  allowDiscoveryByHandle: boolean;
};

/** Отказ сохранить ник — кодом, а не английской строкой сервера (стори 18.2). */
type ShareSettingsProblem = 'taken' | 'invalid' | 'other';

export class ShareSettingsError extends Error {
  readonly problem: ShareSettingsProblem;

  constructor(problem: ShareSettingsProblem, message: string) {
    super(message);
    this.problem = problem;
  }
}

/**
 * Сохранение ника и настроек публичности.
 *
 * <p>Возвращает то, что реально легло в базу: сервер нормализует ввод, и класть в стор
 * напечатанное — значит показывать одно, пока сохранено другое.
 *
 * <p>Ошибку отдаём кодом: сервер отвечает по-английски («Handle already in use»), и раньше
 * этот текст показывался как есть в русском, сербском и французском интерфейсе.
 */
export async function patchUserShareSettings(body: PatchUserShareSettingsBody): Promise<SavedShareSettings> {
  const res = await apiFetch('/api/users/me/share-settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const serverText = await readError(res, tr('profileUpdateFailed'));
    if (res.status === 409) {
      throw new ShareSettingsError('taken', serverText);
    }
    if (res.status === 400) {
      throw new ShareSettingsError('invalid', serverText);
    }
    throw new ShareSettingsError('other', serverText);
  }
  const raw = (await parseJsonBody(res)) as Partial<SavedShareSettings> | null;
  return {
    publicHandle: typeof raw?.publicHandle === 'string' && raw.publicHandle.length > 0 ? raw.publicHandle : null,
    profilePublic: raw?.profilePublic !== false,
    allowDiscoveryByHandle: raw?.allowDiscoveryByHandle !== false,
  };
}

/**
 * «Не беспокоить» (заказ владельца 2026-09-11).
 *
 * <p>Своя ручка, а не поле профиля: профильный PATCH перезаписывает профиль целиком и требует
 * прислать все поля сразу. За один щелчок тумблера это слишком дорого — и опасно: забытое поле
 * там не «осталось прежним», а стёрлось.
 *
 * <p>Возвращает сохранённое сервером состояние: тумблер показывает записанное, а не нажатое.
 */
export async function patchNotificationSettings(doNotDisturb: boolean): Promise<boolean> {
  const res = await apiFetch('/api/users/me/notification-settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doNotDisturb }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('profileUpdateFailed')));
  }
  const raw = (await parseJsonBody(res)) as { doNotDisturb?: boolean } | null;
  return raw?.doNotDisturb === true;
}

/**
 * «Показывать текст сообщения в уведомлениях» (аудит 2026-10-17, PRIV-5) — та же ручка, что и
 * «не беспокоить»; второе поле не шлётся, и сервер его не трогает.
 *
 * <p>Решает сервер: пуш собирает он, и выключенный текст не должен уезжать через FCM/APNs
 * вовсе, а не прятаться на устройстве. Возвращает записанное; сервер постарше поля не знает —
 * тогда ответ «текст виден», то есть ровно то, что он и делает.
 */
export async function patchNotificationShowText(showMessageText: boolean): Promise<boolean> {
  const res = await apiFetch('/api/users/me/notification-settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ showMessageText }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('profileUpdateFailed')));
  }
  const raw = (await parseJsonBody(res)) as { showMessageText?: boolean } | null;
  return raw?.showMessageText !== false;
}

/** Ответ проверки ника: `handle` — каноническая форма, `problem` — причина отказа кодом. */
export type HandleAvailability = {
  handle: string;
  available: boolean;
  problem: 'format' | 'reserved' | 'profanity' | 'taken' | null;
};

/**
 * Свободен ли ник (стори 18.2). У логина такая проверка была с самого начала, у ника — нет,
 * хотя именно ник виден снаружи и занимается навсегда.
 */
export async function checkHandleAvailability(handle: string): Promise<HandleAvailability> {
  const res = await apiFetch(`/api/users/handle-availability?handle=${encodeURIComponent(handle)}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(await readError(res, tr('profileUpdateFailed')));
  const raw = (await res.json()) as Partial<HandleAvailability>;
  return {
    handle: typeof raw?.handle === 'string' ? raw.handle : '',
    available: Boolean(raw?.available),
    problem: (raw?.problem as HandleAvailability['problem']) ?? null,
  };
}

/**
 * Свободные варианты ника, когда выбранный занят.
 *
 * Считает их сервер: у него и так есть ответ на вопрос «что свободно», а клиенту иначе
 * пришлось бы придумывать вариант и спрашивать доступность по одному — по запросу на попытку.
 * Возвращённое свободно на момент ответа, но не забронировано: сохранение всё равно проверяет
 * занятость заново.
 *
 * Пустой список — не ошибка, а «предложить нечего»: экран тогда просто не показывает строку
 * подсказок.
 */
export async function fetchHandleSuggestions(handle: string): Promise<string[]> {
  const res = await apiFetch(
    `/api/users/handle-suggestions?handle=${encodeURIComponent(handle)}`,
    { method: 'GET' },
  );
  if (!res.ok) return [];
  const raw = (await res.json()) as { suggestions?: unknown };
  return Array.isArray(raw?.suggestions) ? raw.suggestions.map(String).filter(Boolean) : [];
}

export function buildPublicProfileUrl(handle: string): string {
  const h = handle.trim();
  return `${PUBLIC_WEB_ORIGIN.replace(/\/$/, '')}/u/${encodeURIComponent(h)}`;
}

export function buildPublicPostUrl(channelSlug: string, postSlug: string): string {
  const base = PUBLIC_WEB_ORIGIN.replace(/\/$/, '');
  return `${base}/c/${encodeURIComponent(channelSlug)}/p/${encodeURIComponent(postSlug)}`;
}

export function buildChannelUrl(channelSlug: string): string {
  return `${PUBLIC_WEB_ORIGIN.replace(/\/$/, '')}/c/${encodeURIComponent(channelSlug)}`;
}

export async function getUserPublicProfile(userId: string): Promise<UserPublic> {
  const res = await apiFetch(`/api/users/${encodeURIComponent(userId)}`, {});
  if (!res.ok) throw new Error(await readError(res, 'User not found'));
  return (await parseJsonBody(res)) as UserPublic;
}

export type AvatarCropPayload = { side: number; offsetX: number; offsetY: number };

/** После нормализации в multipart всегда кладём настоящий Blob или RN-native uri-объект + имя файла. */
type ResolvedMultipartFileSource =
  | { kind: 'blob'; blob: Blob; filename: string }
  | { kind: 'uri'; uri: string; filename: string; mimeType: string };

/**
 * В браузере (react-native-web / webpack) `FormData.append('file', { uri, name, type })` не
 * поддерживается — уходит не файл, Spring: MissingServletRequestPartException {@code file}.
 * На iOS/Android оставляем uri-объект: его понимает нативный networking.
 */
async function resolveMultipartFileSource(
  source:
    | { kind: 'blob'; blob: Blob; filename?: string }
    | { kind: 'uri'; uri: string; filename: string; mimeType: string },
  defaultBlobFilename: string,
): Promise<ResolvedMultipartFileSource> {
  if (source.kind === 'blob') {
    return { kind: 'blob', blob: source.blob, filename: source.filename ?? defaultBlobFilename };
  }
  if (isWeb()) {
    const resp = await fetch(source.uri);
    if (!resp.ok) {
      throw new Error(tr('errorGeneric'));
    }
    const blob = await resp.blob();
    return { kind: 'blob', blob, filename: source.filename };
  }
  return source;
}

function appendMultipartFileField(form: FormData, resolved: ResolvedMultipartFileSource): void {
  if (resolved.kind === 'blob') {
    form.append('file', resolved.blob, resolved.filename);
  } else {
    form.append(
      'file',
      { uri: resolved.uri, name: resolved.filename, type: resolved.mimeType } as unknown as Blob,
      resolved.filename,
    );
  }
}

/** Источник картинки для загрузки аватара: готовый blob из веба или файл с устройства. */
type AvatarUploadSource =
  | { kind: 'blob'; blob: Blob; filename?: string }
  | { kind: 'uri'; uri: string; filename: string; mimeType: string };

/**
 * Ответ на любую операцию с аватаром — новая ревизия.
 *
 * <p>Ревизия здесь не для отчётности: клиент запрашивает файл как `?v=<revision>` и держит его
 * у себя, пока номер не изменится. Число, которое не разобралось, — это отсутствие ответа, а не
 * ноль: подставить сюда ноль значило бы показать чужой кэшированный аватар.
 */
async function readAvatarRevision(res: Response): Promise<{ avatarRevision: number }> {
  if (!res.ok) {
    throw new Error(await readError(res, tr('errorGeneric')));
  }
  const raw = (await res.json()) as { avatarRevision?: unknown };
  const rev = typeof raw.avatarRevision === 'number' ? raw.avatarRevision : Number(raw.avatarRevision);
  if (!Number.isFinite(rev)) {
    throw new Error(tr('errorGeneric'));
  }
  return { avatarRevision: rev };
}

/**
 * Загрузка аватара по указанному адресу — своего или чата.
 *
 * <p>Два вызывающих отличались только адресом, всё остальное — проверка токена, разбор
 * источника, сборка multipart и повторная сборка при ретрае — было переписано дважды.
 *
 * <p>`rebuildFormData` обязателен: `FormData` одноразовая, и при повторе запроса после
 * обновления токена вторая отправка ушла бы с пустым телом.
 */
async function uploadAvatarTo(
  path: string,
  source: AvatarUploadSource,
  crop: AvatarCropPayload,
): Promise<{ avatarRevision: number }> {
  const access = await readAccess();
  if (!access) {
    const fr = await performTokenRefresh();
    if (!fr.ok && fr.clearedSession) {
      throw new Error(tr('sessionExpired'));
    }
  }
  const resolved = await resolveMultipartFileSource(source, 'avatar.jpg');
  const buildFormData = (): FormData => {
    const form = new FormData();
    appendMultipartFileField(form, resolved);
    form.append('crop', JSON.stringify(crop));
    return form;
  };
  // Тот же длинный таймаут, что и у вложений, а не общие 30 секунд: живая (GIF) аватарка
  // уезжает файлом как есть и весит до 8 МБ, и на мобильной сети это дольше половины минуты.
  // С обычным таймаутом запрос обрывался на полпути, до сервера доезжал обрезанный multipart —
  // в журнале это «Failed to parse multipart servlet request», а у человека «не добавляется».
  const res = await apiFetch(
    path,
    { method: 'POST', body: buildFormData() },
    { rebuildFormData: buildFormData, timeoutMs: UPLOAD_FETCH_TIMEOUT_MS },
  );
  return readAvatarRevision(res);
}

export async function uploadOwnAvatar(
  source: AvatarUploadSource,
  crop: AvatarCropPayload,
): Promise<{ avatarRevision: number }> {
  return uploadAvatarTo('/api/users/me/avatar', source, crop);
}

export async function deleteOwnAvatar(): Promise<{ avatarRevision: number }> {
  return readAvatarRevision(await apiFetch('/api/users/me/avatar', { method: 'DELETE' }));
}

export async function uploadChatAvatar(
  chatId: string,
  source: AvatarUploadSource,
  crop: AvatarCropPayload,
): Promise<{ avatarRevision: number }> {
  return uploadAvatarTo(`/api/chats/${encodeURIComponent(chatId)}/avatar`, source, crop);
}

export async function deleteChatAvatar(chatId: string): Promise<{ avatarRevision: number }> {
  return readAvatarRevision(
    await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/avatar`, { method: 'DELETE' }),
  );
}

export async function patchOwnAvatarShape(shape: string): Promise<void> {
  const res = await apiFetch('/api/users/me/avatar-shape', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shape }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, tr('errorGeneric')));
  }
}

export async function updateUiLocaleOnServer(locale: string): Promise<void> {
  const res = await apiFetch('/api/users/me/ui-locale', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locale }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, 'Could not save language preference'));
  }
}

export async function loginRequest(login: string, password: string, captchaToken?: string) {
  breadcrumb('auth', 'попытка входа');
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      // Заголовок ставится и здесь, хотя запрос идёт мимо apiFetch: именно на этой ручке он
      // нужнее всего — «не могу войти на андроиде» и «не могу войти в вебе» разбираются
      // по-разному, а в журнале сервера они до сих пор выглядели одинаково (OBS-1).
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Client': CLIENT_HEADER,
      },
      body: JSON.stringify({ login, password, captchaToken }),
    });
  } catch (networkError) {
    // До сервера не дошли: у этой неудачи нет и не может быть серверной стороны.
    void reportError('auth', networkError, {
      level: 'warning',
      context: { исход: 'сеть-недоступна' },
    });
    throw networkError;
  }
  noteRequestId(res.headers?.get?.('X-Request-Id'));
  const data = (await parseJsonBody(res)) as { error?: string } | AuthResponse | null;
  if (!res.ok) {
    // Отказ входа — `warning`, а не `error`: опечатка в пароле не должна поднимать
    // телеграм-бота. Но в журнале она теперь есть с обеих сторон, и `requestId` сшивает их.
    void reportError('auth', new Error(`login failed: ${res.status}`), {
      level: 'warning',
      context: {
        код: res.status,
        // С AUTH-14 `error` у отказа проверки стал одинаковым для всех полей, поэтому в отчёт
        // едут признаки: без них «login failed: 400» снова стало бы неразличимым.
        причина:
          data && typeof data === 'object' && 'rule' in data && typeof data.rule === 'string'
            ? `${(data as ValidationErrorBody).field ?? '-'}: ${data.rule}`
            : data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
              ? data.error
              : '-',
      },
    });
    // 429 приходит от брутфорс-ограничителя; текст сервера намеренно одинаков для любого
    // логина (чтобы не выдавать существование аккаунта) и не локализован — переводим здесь.
    if (res.status === 429) {
      throw new Error(tr('loginTooManyAttempts'));
    }
    // 428: сервер требует CAPTCHA (AUTH-4). Отдельный класс ошибки, потому что экран должен
    // показать виджет и дать повторить, а не сообщить «неверный пароль» на верном пароле.
    if (res.status === 428) {
      throw new CaptchaRequiredError();
    }
    // AUTH-14. Сначала — признаки отказа проверки (`field`/`rule`): именно на этом пути к
    // ревьюеру Apple уезжал сырой текст валидатора на китайском. `data.error` остаётся
    // запасным для ответов, которые валидацией не являются (например, «Account deleted»).
    const validationKey = validationMessageKey(data as ValidationErrorBody | null);
    const msg = validationKey
      ? tr(validationKey)
      : data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error
        : tr('loginFailed');
    // С кодом: «неверный логин или пароль» (401) экран переводит сам — сервер отвечает
    // по-английски «Invalid credentials» в любом языке интерфейса (аудит 2026-10-04).
    throw new HttpStatusError(res.status, msg);
  }
  return data as AuthResponse;
}

/**
 * Сервер требует CAPTCHA (AUTH-4). Своим классом, а не текстом сообщения: экран отличает
 * этот случай от «неверный пароль» и показывает виджет вместо ошибки, а сравнивать строки
 * ответа значило бы завязаться на их формулировки в четырёх локалях.
 */
export class CaptchaRequiredError extends Error {
  constructor() {
    super('captcha_required');
    this.name = 'CaptchaRequiredError';
  }
}

/**
 * Настройки, которые клиент обязан знать до входа (AUTH-2). Режим регистрации живёт на
 * сервере: зашитый в клиент ответ разошёлся бы с ним при первом переключении, и человек
 * упирался бы в 403 уже после заполнения формы.
 *
 * Без токена и без падения: экран входа обязан открыться и при недоступном сервере — просто
 * без ссылки «Создать аккаунт».
 */
export async function fetchPublicConfig(): Promise<PublicConfig | null> {
  try {
    const res = await fetch(`${API_BASE}/api/public/config`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return (await res.json()) as PublicConfig;
  } catch {
    return null;
  }
}

/**
 * Самостоятельная регистрация (AUTH-2). Токенов в ответе нет: пока адрес не подтверждён,
 * входить некуда, — и, что важнее, ответ обязан быть одинаковым для занятого и свободного
 * адреса, а выданные токены выдали бы разницу.
 */
export async function registerAccount(
  email: string,
  password: string,
  displayName: string,
  captchaToken?: string,
): Promise<{ verificationRequired: boolean }> {
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password, displayName, captchaToken }),
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error(tr('loginTooManyAttempts'));
    if (res.status === 428) throw new CaptchaRequiredError();
    throw new Error(await readError(res, tr('registerFailed')));
  }
  return (await res.json()) as { verificationRequired: boolean };
}

/**
 * Повторное письмо подтверждения. Сервер отвечает 204 всегда: адреса может не быть, он может
 * быть уже подтверждён, а может упереться в паузу между письмами — снаружи все три случая
 * обязаны быть неотличимы (AUTH-3).
 */
export async function resendVerification(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok && res.status !== 429) {
    throw new Error(await readError(res, tr('errorGeneric')));
  }
}

/** Запрос ссылки на сброс пароля. Ответ ничего не говорит о существовании адреса (AUTH-5). */
export async function forgotPassword(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok && res.status !== 429) {
    throw new Error(await readError(res, tr('errorGeneric')));
  }
}

/** Новый пароль по ссылке из письма. Отзывает все сессии — в этом смысл операции (AUTH-5). */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  if (!res.ok) {
    throw await httpStatusError(res, tr('resetFailed'));
  }
}

/**
 * Гасит сессию на сервере: refresh-токен и вся его цепочка ротаций удаляются из базы.
 * Best-effort — локальный выход не должен зависеть от сети, поэтому ошибки глотаются;
 * сервер отвечает 204 и на неизвестный токен.
 */
export async function logoutRequest(): Promise<void> {
  const refresh = await readRefresh();
  if (!refresh) return;
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
  } catch {
    // Нет сети — выходим локально; серверная строка доживёт до своего срока.
  }
}

export async function searchUsersByNickname(q: string): Promise<UserPublic[]> {
  const qq = q.trim();
  // Сервер на запрос короче двух символов отвечает пустым списком (SEC-29) — не тратим лимит.
  if (qq.length < 2) return [];
  const res = await apiFetch(`/api/users/search?q=${encodeURIComponent(qq)}`, { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, tr('searchFailed')));
  const raw: unknown = await res.json();
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row: unknown) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const id = o.id != null ? String(o.id) : '';
      const displayName = o.displayName != null ? String(o.displayName) : '';
      if (!id) return null;
      return {
        id,
        displayName,
        hasAvatar: o.hasAvatar === true,
        avatarRevision: typeof o.avatarRevision === 'number' ? o.avatarRevision : Number(o.avatarRevision) || 0,
        avatarShape: o.avatarShape != null ? String(o.avatarShape) : null,
        publicHandle: o.publicHandle != null && o.publicHandle !== '' ? String(o.publicHandle) : null,
      };
    })
    .filter((x): x is UserPublic => x != null);
}

export async function checkLoginAvailability(login: string): Promise<boolean> {
  const normalized = login.trim().toLowerCase();
  if (normalized.length < 2) return false;
  const res = await apiFetch(`/api/users/login-availability?login=${encodeURIComponent(normalized)}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(await readError(res, tr('profileUpdateFailed')));
  const raw = (await res.json()) as { available?: unknown };
  return Boolean(raw?.available);
}

export async function fetchChats(): Promise<ChatSummary[]> {
  const res = await apiFetch('/api/chats', { method: 'GET' });
  if (!res.ok) throw new Error('Failed to load chats');
  return res.json();
}

/**
 * «Избранное» пользователя (эпик 8). Создаётся сервером при первом обращении, поэтому это
 * одновременно и «открой», и «заведи»; повторные вызовы возвращают тот же чат.
 */
export async function fetchSavedChat(): Promise<ChatSummary> {
  const res = await apiFetch('/api/chats/saved', { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to open saved messages'));
  return res.json();
}

/**
 * Лента «Избранного» по разделу (стори 8.4). Фильтрует сервер: собрать «Файлы» на клиенте
 * значило бы прокачать в память всю историю ради десятка снимков.
 *
 * Идентификатор чата не передаётся — «Избранное» сервер достаёт по владельцу.
 */
export async function fetchSavedSectionMessages(
  section: string,
  opts?: { limit?: number; before?: string },
): Promise<MessageDto[]> {
  const params = new URLSearchParams();
  params.set('section', section);
  params.set('limit', String(opts?.limit ?? 50));
  if (opts?.before) {
    params.set('before', opts.before);
  }
  const res = await apiFetch(`/api/chats/saved/messages?${params.toString()}`, { method: 'GET' });
  if (!res.ok) throw new Error('Failed to load messages');
  return res.json();
}

/**
 * Закреплённые заметки «Избранного» целиком, вне страничной ленты (стори 9.1).
 *
 * Отдельная ручка, а не выборка из уже загруженной страницы: страница — это последние
 * несколько десятков сообщений раздела, и заметка, закреплённая месяц назад, в неё просто
 * не попадает. Полоса, собранная из страницы, молча теряла бы ровно те заметки, ради которых
 * закрепление и существует.
 */
export async function fetchPinnedNotes(): Promise<MessageDto[]> {
  const res = await apiFetch('/api/chats/saved/notes/pinned', { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load pinned notes'));
  return res.json();
}

/**
 * Очистка «Избранного» целиком (заказ владельца 2026-08-31).
 *
 * <p>Одним запросом, а не циклом удалений с клиента: там бывают сотни карточек, и сотня
 * запросов подряд — это и минуты ожидания, и половина удалённого при обрыве связи.
 *
 * @returns сколько сообщений унесло — чтобы отличить «очищено» от «и так было пусто».
 */
export async function clearSavedMessages(): Promise<number> {
  const res = await apiFetch('/api/chats/saved/messages', { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to clear saved messages'));
  const body = (await res.json()) as { removed?: number };
  return typeof body?.removed === 'number' ? body.removed : 0;
}

/** Копия сообщения в «Избранное» с обратной ссылкой на источник (стори 8.3). */
export async function saveMessageToSaved(messageId: string): Promise<MessageDto> {
  const res = await apiFetch(`/api/chats/saved/messages/from/${encodeURIComponent(messageId)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to save message'));
  return res.json();
}

export async function createDirect(otherUserId: string): Promise<ChatSummary> {
  const res = await apiFetch('/api/chats/direct', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ otherUserId }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to create chat'));
  return res.json();
}

export async function createGroup(name: string, memberIds: string[]): Promise<ChatSummary> {
  const res = await apiFetch('/api/chats/groups', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, memberIds }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to create group'));
  return res.json();
}

export async function listGroupMembers(chatId: string): Promise<GroupMember[]> {
  const res = await apiFetch(`/api/chats/groups/${encodeURIComponent(chatId)}/members`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load members'));
  return res.json();
}

export async function addGroupMember(chatId: string, userId: string): Promise<GroupMember[]> {
  const res = await apiFetch(`/api/chats/groups/${encodeURIComponent(chatId)}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to add member'));
  return res.json();
}

export async function removeGroupMember(chatId: string, userId: string): Promise<GroupMember[]> {
  const res = await apiFetch(
    `/api/chats/groups/${encodeURIComponent(chatId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to remove member'));
  return res.json();
}

export async function changeGroupMemberRole(
  chatId: string,
  userId: string,
  role: 'ADMIN' | 'MEMBER',
): Promise<GroupMember[]> {
  const res = await apiFetch(
    `/api/chats/groups/${encodeURIComponent(chatId)}/members/${encodeURIComponent(userId)}/role`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to change role'));
  return res.json();
}

export async function updateGroup(
  chatId: string,
  name: string,
  description?: string,
): Promise<void> {
  const res = await apiFetch(`/api/chats/groups/${encodeURIComponent(chatId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description: description ?? '' }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to save group info'));
}

export async function leaveGroup(chatId: string): Promise<void> {
  const res = await apiFetch(`/api/chats/groups/${encodeURIComponent(chatId)}/leave`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to leave the group'));
}

export async function createChannel(
  name: string,
  description: string,
  /** Invited as ordinary subscribers (MEMBER), not editors. */
  memberIds: string[],
  opts?: {
    allowSubscriberReactions?: boolean;
    allowSubscriberComments?: boolean;
    slug?: string;
  },
): Promise<ChatSummary> {
  const res = await apiFetch('/api/chats/channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      description,
      memberIds,
      allowSubscriberReactions: Boolean(opts?.allowSubscriberReactions),
      allowSubscriberComments: Boolean(opts?.allowSubscriberComments),
      ...(opts?.slug != null && opts.slug.trim().length > 0 ? { slug: opts.slug.trim() } : {}),
    }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to create channel'));
  return res.json();
}

export async function fetchChannelsDirectory(q?: string): Promise<ChannelDirectoryItem[]> {
  const params = new URLSearchParams();
  if (q && q.trim().length > 0) {
    params.set('q', q.trim());
  }
  const suffix = params.toString();
  const res = await apiFetch(`/api/chats/channels/directory${suffix ? `?${suffix}` : ''}`, { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load channels'));
  return res.json();
}

export type PublicChannelPreview = {
  id: string;
  handle: string;
  title: string;
  description?: string | null;
  subscriberCount: number;
  chatHasAvatar: boolean;
  chatAvatarRevision?: number | null;
  chatAvatarShape?: string | null;
  recentPosts: PublicChannelPostCard[];
};

/**
 * Карточка статьи на публичной витрине.
 *
 * <p>`coverUrl` — уже готовый публичный адрес, а не путь `/api/attachments/<uuid>`. Разница не
 * косметическая: внутренний путь требует членства в канале, то есть на витрине, которую и
 * смотрит ещё не подписавшийся, все обложки приезжали бы битыми.
 */
export type PublicChannelPostCard = {
  slug: string;
  title: string;
  previewText?: string | null;
  coverUrl?: string | null;
  authorDisplayName?: string | null;
  publishedAt?: string | null;
};

/** Опубликованная статья, открытая без входа и без подписки. */
export type PublicChannelPost = {
  channelHandle: string;
  channelTitle: string;
  channelHasAvatar: boolean;
  channelAvatarRevision?: number | null;
  subscriberCount: number;
  slug: string;
  title: string;
  authorDisplayName?: string | null;
  publishedAt?: string | null;
  coverUrl?: string | null;
  previewText?: string | null;
  content: ChannelPostContent;
};

/**
 * Заглянуть в канал до подписки (п. 6.6). Эндпоинт публичный — тот же, которым живут
 * веб-ссылки на каналы, — поэтому идёт без авторизации и работает только по слагу.
 */
export async function fetchPublicChannelPreview(handle: string): Promise<PublicChannelPreview> {
  const res = await fetch(`${API_BASE}/api/public/channels/${encodeURIComponent(handle)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(tr('channelPreviewUnavailable'));
  }
  return (await res.json()) as PublicChannelPreview;
}

/**
 * Тело статьи по паре «адрес канала + адрес статьи» — без входа и без подписки.
 *
 * <p>Ровно эта ручка и даёт шаг воронки «прочитал → понравилось → подписался»: до неё прочитать
 * статью, не будучи членом канала, было нельзя ничем, и подписываться приходилось вслепую.
 *
 * <p>`null` вместо исключения на 404: «статьи нет или она снята с публикации» — нормальный
 * ответ, а не сбой, и у вызывающего есть что показать вместо неё.
 */
export async function fetchPublicChannelPost(
  handle: string,
  slug: string,
): Promise<PublicChannelPost | null> {
  const res = await fetch(
    `${API_BASE}/api/public/channels/${encodeURIComponent(handle)}/posts/${encodeURIComponent(slug)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(tr('channelPostLoadFailed'));
  return (await res.json()) as PublicChannelPost;
}

export type PublicUserPreview = {
  id: string;
  displayName: string;
  publicHandle: string;
  profilePublic: boolean;
  allowDiscoveryByHandle: boolean;
  hasAvatar: boolean;
  avatarRevision: number;
  avatarShape?: string | null;
};

/**
 * Профиль по публичному нику — тот же эндпоинт, которым живёт страница `/u/<ник>` (стори 18.3).
 *
 * <p>Идёт без авторизации: по ссылке приходит и тот, у кого аккаунта ещё нет. `null` вместо
 * исключения на 404 — потому что «профиль скрыт» здесь не ошибка, а один из двух нормальных
 * ответов, и у вызывающего есть второй путь (поиск по нику для уже вошедших).
 */
export async function fetchPublicUserPreview(handle: string): Promise<PublicUserPreview | null> {
  const res = await fetch(`${API_BASE}/api/public/profiles/${encodeURIComponent(handle)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(tr('errorGeneric'));
  return (await res.json()) as PublicUserPreview;
}

export async function subscribeChannel(chatId: string): Promise<ChatSummary> {
  const res = await apiFetch(`/api/chats/channels/${encodeURIComponent(chatId)}/subscribe`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to subscribe channel'));
  return res.json();
}

export async function unsubscribeChannel(chatId: string): Promise<void> {
  const res = await apiFetch(`/api/chats/channels/${encodeURIComponent(chatId)}/subscribe`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to unsubscribe channel'));
}

export async function updateChannel(
  chatId: string,
  payload: {
    name: string;
    description: string;
    allowSubscriberReactions: boolean;
    allowSubscriberComments: boolean;
    slug?: string;
  },
): Promise<ChatSummary> {
  const res = await apiFetch(`/api/chats/channels/${encodeURIComponent(chatId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to update channel'));
  return res.json();
}

export async function addChannelEditor(chatId: string, userId: string): Promise<ChatSummary> {
  const res = await apiFetch(
    `/api/chats/channels/${encodeURIComponent(chatId)}/editors/${encodeURIComponent(userId)}`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to add channel editor'));
  return res.json();
}

export async function removeChannelEditor(chatId: string, userId: string): Promise<ChatSummary> {
  const res = await apiFetch(
    `/api/chats/channels/${encodeURIComponent(chatId)}/editors/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to remove channel editor'));
  return res.json();
}

export async function fetchChannelEditors(chatId: string): Promise<UserPublic[]> {
  const res = await apiFetch(`/api/chats/channels/${encodeURIComponent(chatId)}/editors`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load channel editors'));
  const raw: unknown = await res.json();
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row: unknown) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const id = o.id != null ? String(o.id) : '';
      const displayName = o.displayName != null ? String(o.displayName) : '';
      if (!id) return null;
      return {
        id,
        displayName,
        hasAvatar: o.hasAvatar === true,
        avatarRevision: typeof o.avatarRevision === 'number' ? o.avatarRevision : Number(o.avatarRevision) || 0,
        avatarShape: o.avatarShape != null ? String(o.avatarShape) : null,
        publicHandle: o.publicHandle != null && o.publicHandle !== '' ? String(o.publicHandle) : null,
      };
    })
    .filter((x): x is UserPublic => x != null);
}

export async function deleteChat(chatId: string): Promise<void> {
  const res = await apiFetch(`/api/chats/${chatId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to delete chat'));
}

/**
 * «Удалить у меня» (аудит удобства 2026-10-04): личный чат пропадает только из своего списка,
 * у собеседника переписка остаётся. Вернётся сам с первым новым сообщением.
 */
export async function hideChatForMe(chatId: string): Promise<void> {
  const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/hide`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to delete chat'));
}

/**
 * `before` листает вглубь истории, `after` добирает только хвост новее известного момента —
 * второе нужно после возврата из фона и после реконнекта WebSocket, чтобы не перекачивать
 * страницу целиком (в ней могут лежать base64-payload'ы видео).
 */
export async function fetchMessages(
  chatId: string,
  opts?: { limit?: number; before?: string; after?: string },
): Promise<MessageDto[]> {
  const limit = opts?.limit ?? 80;
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (opts?.before) {
    params.set('before', opts.before);
  }
  if (opts?.after) {
    params.set('after', opts.after);
  }
  const res = await apiFetch(`/api/chats/${chatId}/messages?${params.toString()}`, { method: 'GET' });
  if (!res.ok) throw new Error('Failed to load messages');
  return res.json();
}

/**
 * Одно сообщение — оригинал для цитаты ответа, не попавший в загруженную страницу ленты.
 * `null` — сообщения нет (удалено): сервер отвечает 404 одинаково на удалённое и на чужое.
 */
export async function fetchMessage(chatId: string, messageId: string): Promise<MessageDto | null> {
  const res = await apiFetch(
    `/api/chats/${chatId}/messages/${encodeURIComponent(messageId)}`,
    { method: 'GET' },
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await readError(res, 'Failed to load message'));
  return res.json();
}

export async function listChannelPosts(
  channelId: string,
  opts?: { page?: number; size?: number; includeDrafts?: boolean },
): Promise<ChannelPostListItemDto[]> {
  const params = new URLSearchParams();
  params.set('page', String(opts?.page ?? 0));
  params.set('size', String(opts?.size ?? 20));
  if (opts?.includeDrafts) {
    params.set('includeDrafts', 'true');
  }
  const res = await apiFetch(
    `/api/channels/${encodeURIComponent(channelId)}/posts?${params.toString()}`,
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to load channel posts'));
  const raw = (await res.json()) as { content?: unknown };
  const content = raw.content;
  if (!Array.isArray(content)) return [];
  return content as ChannelPostListItemDto[];
}

export async function createChannelPost(
  channelId: string,
  body: {
    title: string;
    slug?: string;
    coverImageUrl?: string | null;
    content: ChannelPostContent;
    status?: ChannelPostStatus;
  },
): Promise<ChannelPostDto> {
  const res = await apiFetch(`/api/channels/${encodeURIComponent(channelId)}/posts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to create post'));
  return res.json();
}

/**
 * Статья по её адресу внутри канала — вход по ссылке для того, кто уже подписан.
 *
 * <p>`null` на 404: «этой статьи в канале нет» здесь не сбой, а нормальный ответ, после
 * которого экран пробует публичный путь.
 */
export async function getChannelPostBySlug(
  channelId: string,
  slug: string,
  countView = true,
): Promise<ChannelPostDto | null> {
  const q = countView ? '' : '?countView=false';
  const res = await apiFetch(
    `/api/channels/${encodeURIComponent(channelId)}/posts/by-slug/${encodeURIComponent(slug)}${q}`,
    { method: 'GET' },
  );
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) throw new Error(await readError(res, 'Failed to load post'));
  return res.json();
}

export async function getChannelPost(postId: string, countView = true): Promise<ChannelPostDto> {
  const q = countView ? '' : '?countView=false';
  const res = await apiFetch(`/api/channel-posts/${encodeURIComponent(postId)}${q}`, { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load post'));
  return res.json();
}

export async function listChannelPostComments(
  postId: string,
  opts?: { cursor?: string | null; limit?: number },
): Promise<ChannelPostCommentPage> {
  const params = new URLSearchParams();
  if (opts?.cursor) params.set('cursor', opts.cursor);
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  const q = params.toString();
  const res = await apiFetch(
    `/api/channel-posts/${encodeURIComponent(postId)}/comments${q ? `?${q}` : ''}`,
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to load comments'));
  return res.json();
}

export async function createChannelPostComment(
  postId: string,
  body: { text: string; parentCommentId?: string | null },
): Promise<ChannelPostCommentDto> {
  const res = await apiFetch(`/api/channel-posts/${encodeURIComponent(postId)}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to post comment'));
  return res.json();
}

/**
 * Модерация обсуждения (волна 3, D1/D2).
 *
 * <p>Все четыре ручки были на сервере с самого начала и не вызывались ниоткуда: удалить чужую
 * грубость, стереть собственный комментарий, одобрить придержанный премодерацией или забанить
 * автора было нечем. Премодерация при этом была ловушкой без выхода — включённая, она прятала
 * комментарии навсегда.
 */
export async function updateChannelPostComment(
  postId: string,
  commentId: string,
  text: string,
): Promise<ChannelPostCommentDto> {
  const res = await apiFetch(
    `/api/channel-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to edit comment'));
  return res.json();
}

export async function deleteChannelPostComment(postId: string, commentId: string): Promise<void> {
  const res = await apiFetch(
    `/api/channel-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to delete comment'));
}

export async function approveChannelPostComment(
  postId: string,
  commentId: string,
): Promise<ChannelPostCommentDto> {
  const res = await apiFetch(
    `/api/channel-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/approve`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to approve comment'));
  return res.json();
}

export async function banUserFromChannelComments(postId: string, userId: string): Promise<void> {
  const res = await apiFetch(
    `/api/channel-posts/${encodeURIComponent(postId)}/comments/moderation/ban-user`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to ban user'));
}

/**
 * Звук чата — на сервере (волна 3, C1).
 *
 * <p>До этого настройка жила только на устройстве: на iOS она не работала вовсе (алерт рисует
 * система из notification-блока, до JS-проверки дело не доходит) и не переезжала между
 * устройствами.
 */
export async function setChatMuted(
  chatId: string,
  muted: boolean,
  /** Срок заглушения (аудит удобства 2026-10-04); не задан — навсегда. */
  mutedUntil?: Date | null,
): Promise<ChatSummary> {
  const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/mute`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(muted && mutedUntil ? { muted, mutedUntil: mutedUntil.toISOString() } : { muted }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to change notifications'));
  return res.json();
}

export async function toggleChannelPostCommentReaction(
  postId: string,
  commentId: string,
  reactionKey: string,
): Promise<ChannelPostCommentDto> {
  const res = await apiFetch(
    `/api/channel-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/reactions`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reactionKey }),
    },
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to react'));
  return res.json();
}

export async function publishChannelPost(postId: string): Promise<ChannelPostDto> {
  const res = await apiFetch(`/api/channel-posts/${encodeURIComponent(postId)}/publish`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to publish post'));
  return res.json();
}

/**
 * Правка статьи. Сервер сам обновит карточку-объявление в ленте канала, если пост опубликован,
 * и сделает это без повторного пуша подписчикам — правка опечатки не должна звонить всем ещё раз.
 *
 * <p>Поля необязательные и означают «не трогать», кроме одного: `coverImageUrl: ''` — это
 * «убрать обложку» (санитайзер на пустой строке возвращает null). Разница важна, потому что
 * отсутствие ключа и пустая строка здесь значат противоположное.
 */
export async function updateChannelPost(
  postId: string,
  body: {
    title?: string;
    slug?: string;
    coverImageUrl?: string | null;
    content?: ChannelPostContent;
  },
): Promise<ChannelPostDto> {
  const res = await apiFetch(`/api/channel-posts/${encodeURIComponent(postId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to save post'));
  return res.json();
}

/** Удаление статьи вместе с её объявлением в ленте канала. Необратимо. */
export async function deleteChannelPost(postId: string): Promise<void> {
  const res = await apiFetch(`/api/channel-posts/${encodeURIComponent(postId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to delete post'));
}

/** Вернуть опубликованную статью в черновики: объявление из ленты исчезает, текст остаётся. */
export async function unpublishChannelPost(postId: string): Promise<ChannelPostDto> {
  const res = await apiFetch(`/api/channel-posts/${encodeURIComponent(postId)}/unpublish`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to unpublish post'));
  return res.json();
}

export async function markChatRead(chatId: string): Promise<void> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/read`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to mark chat as read'));
}

export async function sendMessage(chatId: string, ciphertext: string): Promise<MessageDto> {
  const res = await apiFetch(`/api/chats/${chatId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ciphertext }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Send failed'));
  return res.json();
}

export async function sendMessageReply(
  chatId: string,
  ciphertext: string,
  replyToMessageId: string,
): Promise<MessageDto> {
  const res = await apiFetch(`/api/chats/${chatId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ciphertext, replyToMessageId }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Send failed'));
  return res.json();
}

/**
 * Переписать текст уже отправленного сообщения.
 *
 * <p>Одна ручка на два правила, развилка — на сервере по типу чата: заметка в собственном
 * «Избранном» (стори 9.1) правится без срока и без пометки, обычное сообщение — только своё,
 * только текст, только двое суток и с пометкой «изменено» у собеседника.
 *
 * <p>Отдельная ручка, а не повторная отправка: автосохранение заметки раз в 800 мс превратило
 * бы её в ленту из десятков сообщений, а правка в переписке обязана остаться тем же баблом на
 * том же месте — иначе она читается как новое сообщение.
 */
export async function editMessageCiphertext(
  chatId: string,
  messageId: string,
  ciphertext: string,
): Promise<MessageDto> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/${messageId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ciphertext }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Edit failed'));
  return res.json();
}

/**
 * Удалить сообщение у всех участников. Второго режима («убрать только у себя») нет: у
 * сообщения одна строка на всех, и локальное скрытие потребовало бы таблицы «кто чего не
 * видит» в каждой выборке — ленты, превью списка чатов, поиска, «Избранного».
 *
 * Ответ пустой (204): отдавать удалённое сообщение обратно нечем и незачем. Сервер шлёт всем
 * MESSAGE_DELETED по сокету, и бабл пропадает у собеседника сам.
 */
export async function deleteMessage(chatId: string, messageId: string): Promise<void> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/${messageId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, 'Delete failed'));
}

/**
 * Удаление выделенного одним запросом.
 *
 * <p>Раньше экран удалял выбранное циклом — пятьдесят отмеченных сообщений означали пятьдесят
 * запросов подряд, каждый со своим круговым временем; обрыв связи посередине оставлял половину
 * удалённой, и списку приходилось объяснять это пользователю. Сервер делает всё одной
 * транзакцией: либо уходит всё, либо ничего.
 *
 * <p>{@code POST}, а не {@code DELETE}: телу у DELETE по HTTP никто ничего не обещает — его
 * вправе выбросить и прокси, и клиентская библиотека.
 */
export async function deleteMessages(chatId: string, messageIds: string[]): Promise<void> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/delete-batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messageIds }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Delete failed'));
}

export async function forwardMessages(
  chatId: string,
  targetUserId: string,
  messageIds: string[],
): Promise<MessageDto[]> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/forward`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetUserId, messageIds }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Forward failed'));
  return res.json();
}

export async function addMessageReaction(chatId: string, messageId: string, emoji: string): Promise<MessageDto> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/${messageId}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Reaction failed'));
  return res.json();
}

/** Только автор чеклиста; сервер шлёт обновлённое MESSAGE по WebSocket. */
export async function toggleChecklistItem(
  chatId: string,
  messageId: string,
  itemId: string,
): Promise<MessageDto> {
  const res = await apiFetch(`/api/chats/${chatId}/messages/${messageId}/checklist/toggle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemId }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Checklist update failed'));
  return res.json();
}

export async function removeMessageReaction(chatId: string, messageId: string, emoji: string): Promise<MessageDto> {
  const res = await apiFetch(
    `/api/chats/${chatId}/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`,
    {
    method: 'DELETE',
    },
  );
  if (!res.ok) throw new Error(await readError(res, 'Reaction failed'));
  return res.json();
}

/**
 * Metadata returned by `POST /api/chats/{chatId}/attachments`. The bytes are
 * not part of the response; clients reference them by `id` in v2 chat payloads
 * and fetch them later via `GET /api/attachments/{id}`.
 */
type AttachmentMeta = {
  id: string;
  mimeType: string;
  sizeBytes: number;
  durationMs?: number;
  thumbnailBase64?: string;
  sha256?: string;
  /** Расшифровка голосового (эпик 21): состояние работы. */
  transcriptStatus?: TranscriptStatus;
  /** Язык, на котором распознали. */
  transcriptLang?: string;
  /**
   * Первые ~200 знаков расшифровки. Полный текст — {@link fetchAttachmentTranscript}: вместе со
   * страницей чата он не едет, иначе тридцать длинных голосовых в одном экране истории тянули бы
   * четверть мегабайта текста, который никто не откроет.
   */
  transcriptPreview?: string;
};

/**
 * Состояние расшифровки. {@code none} — её не заводили (не аудио, выключено, старая запись);
 * {@code skipped} — не стали расшифровывать (запись длиннее потолка). Это два разных сообщения
 * на экране, поэтому и два разных значения.
 */
export type TranscriptStatus =
  | 'none'
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'skipped';

export type AttachmentTranscript = {
  id: string;
  status: TranscriptStatus;
  lang: string | null;
  text: string | null;
};

/** Состояние расшифровки без полного текста — то, что нужно баблу при отрисовке. */
export type TranscriptState = {
  id: string;
  status: TranscriptStatus;
  lang: string | null;
  preview: string | null;
};

/**
 * Состояния расшифровки пачкой — под одну страницу чата (эпик 21, стори 21.2).
 *
 * <p>Бабл голосового знает про своё вложение только идентификатор, а состояние нужно ему при
 * отрисовке. Запрос на каждый бабл — это тридцать обращений на один экран истории, поэтому
 * спрашиваем разом про то, что показываем.
 *
 * <p>Ошибка не пробрасывается: без состояний бабл выглядит как до эпика — полоса
 * воспроизведения без строки «Текст». Это хуже, чем с расшифровкой, но лучше, чем упавший чат.
 */
export async function fetchTranscriptStates(
  chatId: string,
  attachmentIds: readonly string[],
): Promise<TranscriptState[]> {
  if (attachmentIds.length === 0) return [];
  try {
    const res = await apiFetch(
      `/api/chats/${encodeURIComponent(chatId)}/attachments/transcript-states`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: attachmentIds }),
      },
    );
    if (!res.ok) return [];
    return (await res.json()) as TranscriptState[];
  } catch {
    return [];
  }
}

/**
 * Полная расшифровка голосового (эпик 21, стори 21.2).
 *
 * <p>Зовётся в момент разворота, а не при отрисовке списка: право на неё то же, что и на сами
 * байты записи, а объём — до нескольких килобайт на сообщение.
 */
export async function fetchAttachmentTranscript(
  attachmentId: string,
): Promise<AttachmentTranscript | null> {
  const res = await apiFetch(
    `/api/attachments/${encodeURIComponent(attachmentId)}/transcript`,
    { method: 'GET' },
  );
  // 404 — вложения нет или мы не в этом чате. Для экрана это одно и то же: текста не будет.
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`transcript failed: ${res.status}`);
  }
  return (await res.json()) as AttachmentTranscript;
}

/**
 * Build the absolute URL to download the bytes of a previously-uploaded
 * attachment. Note: the endpoint requires a Bearer auth header — use
 * {@link buildAttachmentAuthHeaders} when handing the URL to a player /
 * `<Image>` that supports remote sources with headers.
 */
export function buildAttachmentDownloadUrl(attachmentId: string): string {
  return `${API_BASE}/api/attachments/${encodeURIComponent(attachmentId)}`;
}

/**
 * Resolve a short-lived direct (pre-signed R2/CDN) download URL for an
 * attachment, or {@code null} when the server streams it itself (local FS).
 *
 * Why not just download {@link buildAttachmentDownloadUrl} directly on native:
 * that endpoint 302-redirects to a pre-signed R2 URL, and native HTTP clients
 * (iOS NSURLSession via RNFS) forward the {@code Authorization} header to R2,
 * which then rejects with HTTP 400 (pre-signed URLs already carry auth in the
 * query string). We fetch the URL here WITH auth, then download it WITHOUT auth.
 */
type AttachmentDirectSource = {
  /** null when the backend cannot pre-sign; stream through the API instead. */
  url: string | null;
  /**
   * Format according to the attachment row — the source of truth. A voice note
   * recorded in a browser was stored as WebM/Opus and may since have been
   * converted to AAC, while the message payload still carries the original
   * mime. null when the server did not report one.
   */
  mimeType: string | null;
};

export async function fetchAttachmentDirectSource(
  attachmentId: string,
): Promise<AttachmentDirectSource> {
  const res = await apiFetch(
    `/api/attachments/${encodeURIComponent(attachmentId)}/direct-url`,
    { method: 'GET' },
  );
  if (!res.ok) {
    throw new Error(`direct-url failed: ${res.status}`);
  }
  const body = (await res.json()) as { url?: string; mimeType?: string };
  return {
    url: typeof body.url === 'string' && body.url.length > 0 ? body.url : null,
    mimeType:
      typeof body.mimeType === 'string' && body.mimeType.length > 0 ? body.mimeType : null,
  };
}

export function buildUserAvatarDownloadUrl(userId: string, revision: number): string {
  return `${API_BASE}/api/users/${encodeURIComponent(userId)}/avatar?v=${encodeURIComponent(String(revision))}`;
}

export function buildChatAvatarDownloadUrl(chatId: string, revision: number): string {
  return `${API_BASE}/api/chats/${encodeURIComponent(chatId)}/avatar?v=${encodeURIComponent(String(revision))}`;
}

/**
 * Convenience helper for callers that need to attach the current Bearer token
 * to a non-fetch consumer (RN `<Image source={{ uri, headers }}>` etc.).
 * Returns an empty object when no token is stored so the caller never
 * accidentally sends `Authorization: Bearer null`.
 */
export async function buildAttachmentAuthHeaders(): Promise<Record<string, string>> {
  const token = await readAccess();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Upload an attachment for a chat. The mobile caller can pass either a
 * `Blob`/`File` (web) or a `react-native-fs`-style file URI; on native we
 * build a FormData entry with `{ uri, name, type }` that the iOS / Android
 * fetch implementations recognise as a multipart file part.
 *
 * The endpoint requires the caller to be a member of the target chat (server
 * returns 403 otherwise — see {@code AttachmentService.upload}).
 */
export async function uploadAttachment(
  chatId: string,
  source:
    | { kind: 'blob'; blob: Blob; filename?: string }
    | { kind: 'uri'; uri: string; filename: string; mimeType: string },
  opts?: {
    mimeType?: string;
    durationMs?: number;
    thumbnailBase64?: string;
    /** When set, the upload runs over XHR so the caller can render a progress bar. */
    onProgress?: UploadProgressListener;
  },
): Promise<AttachmentMeta> {
  // Fast path (native only): PUT the bytes straight to the storage provider so
  // they don't make the extra hop through our VPS and back out to Cloudflare.
  // Falls back to the multipart endpoint whenever a ticket isn't available.
  if (Platform.OS !== 'web' && source.kind === 'uri') {
    const mime = opts?.mimeType ?? source.mimeType;
    const filePath = source.uri.startsWith('file://') ? source.uri.slice('file://'.length) : source.uri;
    let sizeBytes: number | null = null;
    try {
      const stat = await RNFS.stat(filePath);
      const raw = typeof stat.size === 'number' ? stat.size : Number(stat.size);
      sizeBytes = Number.isFinite(raw) && raw > 0 ? raw : null;
    } catch {
      // Size is only an early cap check server-side; the commit reads the real one.
    }
    const ticket = await requestDirectUploadTicket(chatId, mime, sizeBytes);
    if (ticket) {
      await putFileDirect(ticket, filePath, source.filename, opts?.onProgress);
      return commitDirectUpload(chatId, ticket.ticket, {
        mimeType: mime,
        durationMs: opts?.durationMs,
        thumbnailBase64: opts?.thumbnailBase64,
      });
    }
  }

  const resolved = await resolveMultipartFileSource(source, 'upload.bin');
  const buildFormData = (): FormData => {
    const form = new FormData();
    appendMultipartFileField(form, resolved);
    if (opts?.mimeType) form.append('mimeType', opts.mimeType);
    if (typeof opts?.durationMs === 'number' && Number.isFinite(opts.durationMs)) {
      form.append('durationMs', String(Math.max(0, Math.round(opts.durationMs))));
    }
    if (opts?.thumbnailBase64) form.append('thumbnailBase64', opts.thumbnailBase64);
    return form;
  };

  if (opts?.onProgress) {
    const raw = await apiUpload(`/api/chats/${chatId}/attachments`, buildFormData, {
      timeoutMs: UPLOAD_FETCH_TIMEOUT_MS,
      onProgress: opts.onProgress,
    });
    if (raw.status < 200 || raw.status >= 300) {
      throw new Error(errorFromBody(raw.body, 'Upload failed'));
    }
    return JSON.parse(raw.body) as AttachmentMeta;
  }

  // Do NOT set Content-Type manually; the fetch implementation will fill in
  // the multipart boundary. Manually setting it breaks the multipart parser.
  const res = await apiFetch(
    `/api/chats/${chatId}/attachments`,
    {
      method: 'POST',
      body: buildFormData() as unknown as BodyInit,
    },
    { rebuildFormData: buildFormData, timeoutMs: UPLOAD_FETCH_TIMEOUT_MS },
  );
  if (!res.ok) throw new Error(await readError(res, 'Upload failed'));
  return (await res.json()) as AttachmentMeta;
}

export async function registerVoipPushToken(voipPushToken: string): Promise<void> {
  // iOS-only: PushKit VoIP token. Stored separately from the regular FCM/APNs
  // token because it requires a different APNs HTTP/2 push-type (`voip`) and a
  // different APNs topic suffix (`<bundleId>.voip`).
  const res = await apiFetch('/api/devices/voip-push', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ voipPushToken }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Register VoIP push token failed'));
}

export async function deleteAllMyPushTokens(): Promise<void> {
  try {
    await apiFetch('/api/devices/tokens', { method: 'DELETE' }, { retry: false });
  } catch {
    /* best-effort: токены протухнут сами, не блокируем logout */
  }
}

export async function registerFcmToken(
  fcmToken: string,
  platform: 'android' | 'ios' | 'web',
): Promise<void> {
  // No session (e.g. logout teardown, or an AppState 'active' refresh racing a
  // sign-out): posting the token would 401 and spam the console. Skip cleanly —
  // the token is re-registered on the next sign-in.
  if (!(await readAccess())) {
    return;
  }
  const res = await apiFetch('/api/devices/fcm-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fcmToken, platform }),
  });
  if (!res.ok) {
    /* нет Firebase на устройстве / сеть */
  }
}

function parseContactRow(row: unknown): ContactDto | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const userIdRaw = o.userId ?? o.contactUserId ?? o.id;
  const userId = userIdRaw != null ? String(userIdRaw) : '';
  if (!userId) return null;
  const displayNameRaw = o.displayName ?? o.contactDisplayName ?? o.title ?? o.nickname;
  return {
    userId,
    displayName: displayNameRaw != null ? String(displayNameRaw) : '',
    ...parseAvatarFields(o),
    publicHandle: typeof o.publicHandle === 'string' && o.publicHandle ? o.publicHandle : null,
    online: o.online === true,
  };
}

/** Аватар строки контакта или заявки: поля необязательны, сервер постарше их не шлёт. */
function parseAvatarFields(o: Record<string, unknown>): {
  hasAvatar: boolean;
  avatarRevision: number;
  avatarShape: string | null;
} {
  const revision = Number(o.avatarRevision);
  return {
    hasAvatar: o.hasAvatar === true,
    avatarRevision: Number.isFinite(revision) ? revision : 0,
    avatarShape: typeof o.avatarShape === 'string' && o.avatarShape ? o.avatarShape : null,
  };
}

function parseRequestDirection(value: unknown): ContactRequestDirection {
  const v = value != null ? String(value).toUpperCase() : '';
  return v === 'OUTGOING' ? 'OUTGOING' : 'INCOMING';
}

function parseRequestStatus(value: unknown): ContactRequestStatus {
  const v = value != null ? String(value).toUpperCase() : '';
  if (v === 'APPROVED') return 'APPROVED';
  if (v === 'REJECTED') return 'REJECTED';
  if (v === 'CANCELLED') return 'CANCELLED';
  return 'PENDING';
}

function parseContactRequestRow(row: unknown): ContactRequestDto | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const idRaw = o.id ?? o.requestId;
  const userIdRaw = o.userId ?? o.fromUserId ?? o.targetUserId;
  const id = idRaw != null ? String(idRaw) : '';
  const userId = userIdRaw != null ? String(userIdRaw) : '';
  if (!id || !userId) return null;
  const displayNameRaw = o.displayName ?? o.fromDisplayName ?? o.targetDisplayName ?? o.nickname;
  return {
    id,
    userId,
    displayName: displayNameRaw != null ? String(displayNameRaw) : '',
    direction: parseRequestDirection(o.direction),
    status: parseRequestStatus(o.status),
    createdAt: o.createdAt != null ? String(o.createdAt) : new Date().toISOString(),
    ...parseAvatarFields(o),
  };
}

export async function fetchContacts(): Promise<ContactDto[]> {
  const res = await apiFetch('/api/contacts', { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, tr('contactLoadFailed')));
  const raw: unknown = await res.json();
  if (!Array.isArray(raw)) return [];
  return raw.map(parseContactRow).filter((x): x is ContactDto => x != null);
}

export async function fetchContactRequests(): Promise<ContactRequestDto[]> {
  const res = await apiFetch('/api/contacts/requests', { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, tr('contactRequestLoadFailed')));
  const raw: unknown = await res.json();
  if (!Array.isArray(raw)) return [];
  return raw.map(parseContactRequestRow).filter((x): x is ContactRequestDto => x != null);
}

export async function sendContactRequest(targetUserId: string): Promise<ContactRequestDto> {
  const res = await apiFetch('/api/contacts/requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetUserId }),
  });
  if (!res.ok) throw new Error(await readError(res, tr('contactRequestSendFailed')));
  const raw: unknown = await res.json();
  const parsed = parseContactRequestRow(raw);
  if (!parsed) throw new Error(tr('contactRequestSendFailed'));
  return parsed;
}

export async function approveContactRequest(requestId: string): Promise<void> {
  const res = await apiFetch(`/api/contacts/requests/${encodeURIComponent(requestId)}/accept`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, tr('contactRequestApproveFailed')));
}

export async function rejectContactRequest(requestId: string): Promise<void> {
  const res = await apiFetch(`/api/contacts/requests/${encodeURIComponent(requestId)}/reject`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readError(res, tr('contactRequestRejectFailed')));
}

export async function removeContact(contactUserId: string): Promise<void> {
  const res = await apiFetch(`/api/contacts/${encodeURIComponent(contactUserId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await readError(res, tr('contactRemoveFailed')));
}

export type LinkPreviewDto = {
  url: string;
  host: string;
  title: string;
  description?: string | null;
  /** Исходные адреса чужого сайта — только для старых сборок; эта их не грузит (PRIV-4). */
  imageUrl?: string | null;
  faviconUrl?: string | null;
  /** Те же картинки через наш сервер: `/api/link-preview/image/<токен>` (PRIV-4). */
  imageProxyPath?: string | null;
  faviconProxyPath?: string | null;
};

/**
 * Полный адрес картинки превью на нашем сервере (PRIV-4). Что путь действительно наш, проверяет
 * вызывающий — {@link isLinkPreviewImagePath}.
 */
export function buildLinkPreviewImageUrl(proxyPath: string): string {
  return `${API_BASE}${proxyPath}`;
}

export async function fetchLinkPreview(url: string): Promise<LinkPreviewDto> {
  const res = await apiFetch(`/api/link-preview?url=${encodeURIComponent(url)}`, { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load link preview'));
  return res.json() as Promise<LinkPreviewDto>;
}

/** Встреча в списке создателя (заказ владельца 2026-10-12). Ссылку собирает сервер. */
export type Meeting = {
  chatId: string;
  title: string;
  token: string;
  url: string;
  createdAt: string;
  memberCount: number;
};

export async function listMyMeetings(): Promise<Meeting[]> {
  const res = await apiFetch('/api/meetings', { method: 'GET' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to load meetings'));
  return res.json() as Promise<Meeting[]>;
}

export async function createMeeting(title: string): Promise<Meeting> {
  const res = await apiFetch('/api/meetings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to create meeting'));
  return res.json() as Promise<Meeting>;
}

export async function deleteMeeting(chatId: string): Promise<void> {
  const res = await apiFetch(`/api/meetings/${encodeURIComponent(chatId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to delete meeting'));
}

/**
 * Новая ссылка вместо старой (SEC-39): старая сразу перестаёт открывать встречу, участники
 * остаются. Только создатель встречи.
 */
export async function rotateMeetingLink(chatId: string): Promise<Meeting> {
  const res = await apiFetch(`/api/meetings/${encodeURIComponent(chatId)}/rotate-link`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to rotate meeting link'));
  return res.json() as Promise<Meeting>;
}

/** Админ встречи исключил этого человека: по ссылке он не вернётся, пока его не добавят (SEC-39). */
export class MeetingRemovedError extends Error {
  constructor() {
    super('Removed from this meeting');
    this.name = 'MeetingRemovedError';
  }
}

/**
 * Переход по ссылке на встречу: вступить в её группу (повторно — ничего не меняет) и получить
 * карточку группы для входа в звонок. `null` — встречи нет: удалена или ссылка чужая (в том
 * числе старая, после смены ссылки). {@link MeetingRemovedError} — человека из встречи исключили.
 */
export async function joinMeetingByToken(token: string): Promise<ChatSummary | null> {
  const res = await apiFetch(`/api/meetings/join/${encodeURIComponent(token)}`, { method: 'POST' });
  if (res.status === 404) return null;
  if (res.status === 403) throw new MeetingRemovedError();
  if (!res.ok) throw new Error(await readError(res, 'Failed to join meeting'));
  return res.json() as Promise<ChatSummary>;
}
